import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { isAuthenticated } from '../utils/authHelper';
import AuthModal from '../components/AuthModal';
import DonutChart, { SERIES_COLORS } from '../components/DonutChart';
import { PlatformIcon, PLATFORMS } from '../utils/platformIcons';

/**
 * The public landing page.
 *
 * What it has to do is narrow: convince a creator that money actually arrives
 * here, and then get them into the product without leaving the page. So login
 * and sign-up are dialogs (see `components/AuthModal`), not destinations.
 *
 * The hero answers "where does the money come from" as a picture rather than a
 * list — `EarningStage` puts the creator in the middle and has all four
 * streams physically arrive at them, one at a time, with the balance filling
 * as each lands. Then, before the page explains anything about itself, the
 * comparison table answers the only question a visitor who already has a
 * link-in-bio is actually asking. Everything after that is a *screenshot of
 * the real product* rather than an illustration: the income donut, the escrow
 * timeline and the profile are the same components and layout the signed-in
 * app uses, filled with the mock account defined at the top of this file, and
 * they share one frame behind three tabs rather than taking a full-width
 * section each.
 *
 * Three rules held throughout:
 *
 * 1. **The mock numbers reconcile.** Lifetime net − withdrawn equals available
 *    + pending; the fee column matches the fee quoted in the FAQ (zero, so
 *    gross and net are the same figure on every row); 860 stars at $1 is the
 *    $860 on the breakdown row. One loop of the hero stage adds up to exactly
 *    the $9,800 balance the dashboard shows. A landing page whose own figures
 *    contradict each other is the fastest way to lose the reader who checks,
 *    and that reader is the one worth having.
 * 2. **Everything on screen is labelled as an example.** The dashboard frames
 *    carry an "example account" chip. Numbers that look like a promise and are
 *    not one belong to a different kind of website. Nobody on this page is a
 *    person: the quotes that used to be here were attributed to people who do
 *    not exist, which is a different thing from an illustrated dashboard and
 *    does not belong beside checkable figures.
 * 3. **Every competitor figure is sourced and dated.** `COMPARISON` carries
 *    the month it was last checked and the page prints it. A table of other
 *    people's prices is only worth what its worst row is worth, so each one
 *    keeps the caveat that makes it fair — Ko-fi really does take nothing on
 *    a tip, and the table says so next to its name.
 *
 * Copy is English only, deliberately, and does not go through
 * `translations.js` — see the note in `components/AuthModal`.
 */

/* ─────────────────────────  the example account  ───────────────────────── */

/** The single fee number this page is allowed to quote. Every fee figure below
 *  is derived from it, so the table, the timeline and the FAQ cannot drift.
 *
 *  It is 0, and it must stay in step with `PLATFORM_FEE_PERCENT` in the
 *  backend settings — that is the number the ledger actually applies. The fee
 *  column stays in the breakdown table on purpose: a row that prints −$0.00
 *  next to the gross is a stronger claim than one that omits the column, which
 *  reads as a fee simply not being shown. */
const FEE_PERCENT = 0;
const netOf = (gross) => Math.round(gross * (1 - FEE_PERCENT / 100) * 100) / 100;
const feeOf = (gross) => Math.round(gross * (FEE_PERCENT / 100) * 100) / 100;

const CREATOR = {
    name: 'Elena Marsh',
    handle: 'elenamarsh',
    bio: 'Beauty & lifestyle. Lisbon → everywhere. I answer every message.',
    gradient: 'linear-gradient(135deg,#20c9b5,#0f6f63)',
};

const BALANCE = {
    available: 9800,
    pending: 1450,
    lifetimeNet: 48320,
    withdrawn: 37070,
};

/** Last 30 days. `source` keys match `SERIES_COLORS` so the donut colours hold. */
const BREAKDOWN = [
    { source: 'campaign', label: 'Brand campaigns', gross: 5840, meta: '3 collaborations settled' },
    { source: 'subscription', label: 'Subscriptions', gross: 2940, meta: '147 active subscribers' },
    { source: 'coffee', label: 'Coffees', gross: 1000, meta: '200 coffees this month' },
    // One star, one dollar — the rate the app pays out at.
    { source: 'star', label: 'Star gifts', gross: 860, meta: '860 stars received' },
].map((row) => ({ ...row, fee: feeOf(row.gross), net: netOf(row.gross) }));

/** What each platform keeps from the same $1,000 month.
 *
 *  `monthly` is the subscription a creator must pay to be on that tier;
 *  `cut` is the platform's percentage. `keeps` is DERIVED, never typed —
 *  a hand-entered figure here is how this table drifts out of truth.
 *
 *  Figures verified August 2026 from each platform's public pricing page.
 *  They change; `COMPARISON_CHECKED` is printed under the table so the
 *  reader knows how fresh this is, and so we know when to re-check.
 *
 *  Our own row reads `cut: FEE_PERCENT` for the same reason every other fee
 *  figure on this page does: there is one place that number is written down.
 */
const COMPARISON_BASE = 1000;
const COMPARISON_CHECKED = 'August 2026';

const COMPARISON = [
    {
        name: 'Astreona', plan: 'Free, forever', monthly: 0, cut: FEE_PERCENT, us: true,
        note: '0% on tips, subscriptions, gifts and brand campaigns'
    },
    {
        name: 'Ko-fi', site: 'ko-fi.com', plan: 'Free plan', monthly: 0, cut: 5,
        note: '0% on tips — 5% on memberships and shop sales'
    },
    {
        name: 'Buy Me a Coffee', site: 'buymeacoffee.com', plan: 'Only plan', monthly: 0, cut: 5,
        note: '5% on everything, no paid tier to escape it'
    },
    {
        name: 'Patreon', site: 'patreon.com', plan: 'Pro', monthly: 0, cut: 8,
        note: '5% Lite · 8% Pro · 12% Premium'
    },
    {
        name: 'Linktree', site: 'linktr.ee', plan: 'Pro', monthly: 15, cut: 9,
        note: 'Premium removes the 9% — for $35/mo'
    },
    {
        name: 'Beacons', site: 'beacons.ai', logo: '/compare/beacons.webp', plan: 'Creator', monthly: 10, cut: 9,
        note: 'Creator Plus removes the 9% — for $30/mo'
    },
    {
        name: 'Stan Store', site: 'stan.store', logo: '/compare/stanstore.png', plan: 'Creator', monthly: 29, cut: 0,
        note: '0% cut, but no free tier at all'
    },
    {
        name: 'Collabstr', site: 'collabstr.com', logo: '/compare/collabstr.webp', plan: 'Creator side', monthly: 0, cut: 15,
        note: 'And the brand pays 10% on top of your 15%'
    },
].map((row) => {
    const keeps = Math.round((COMPARISON_BASE * (1 - row.cut / 100) - row.monthly) * 100) / 100;
    return { ...row, keeps, lost: Math.round((COMPARISON_BASE - keeps) * 100) / 100 };
});

/** Three competitor figures the page quotes outside the table, kept here
 *  rather than typed into the copy that quotes them. Patreon's is its Premium
 *  tier — the "up to" in the closing CTA, named in that row's note but not the
 *  tier the table compares against. Collabstr bills both sides, and only the
 *  creator's 15% fits under a column headed "their cut". */
const PATREON_MAX_CUT = 12;
const COLLABSTR_BRAND_CUT = 10;
const COLLABSTR_EXAMPLE = 2000;

/** A competitor row by name, so a figure quoted in prose elsewhere on the page
 *  cannot disagree with the same figure in the table. */
const comparisonRow = (name) => COMPARISON.find((row) => row.name === name);

const SHOWCASE = [
    {
        id: 1, title: 'Summer Fragrance Launch', brand: 'Lumière Beauty', budget: 4500,
        platforms: ['Instagram', 'TikTok'], formats: '2 Reels + 1 Story set',
        applicants: 12, rating: 4.9, deals: 31, hot: true,
    },
    {
        id: 2, title: '30-Day Fitness Challenge', brand: 'Kinetic App', budget: 6200,
        platforms: ['TikTok', 'YouTube'], formats: '4 short-form videos over 30 days',
        applicants: 28, rating: 4.8, deals: 64, hot: true,
    },
    {
        id: 3, title: 'Wireless Audio Deep-Dive', brand: 'Nova Audio', budget: 3800,
        platforms: ['YouTube'], formats: '1 long-form integration',
        applicants: 9, rating: 5.0, deals: 18, hot: false,
    },
    {
        id: 4, title: 'Specialty Coffee Unboxing', brand: 'Roast & Co', budget: 1900,
        platforms: ['Instagram'], formats: '1 Reel + carousel',
        applicants: 17, rating: 4.7, deals: 42, hot: false,
    },
];

const RATE_CARD = [
    { id: 1, label: 'Instagram Reel', note: 'Concept, shoot, edit · 1 revision', price: 1200 },
    { id: 2, label: 'Story set (3 frames)', note: 'Link sticker included', price: 450 },
    { id: 3, label: 'TikTok video', note: 'Trend-led, sound-on', price: 950 },
    { id: 4, label: 'UGC pack', note: '3 raw clips, no posting', price: 1800 },
];

const SOCIALS = [
    { platform: 'instagram', handle: `@${CREATOR.handle}`, followers: '128K' },
    { platform: 'tiktok', handle: `@${CREATOR.handle}`, followers: '46.2K' },
    { platform: 'youtube', handle: `@${CREATOR.handle}`, followers: '10.4K' },
];

const FAQ = [
    {
        q: 'What does Astreona cost?',
        // Derived, not typed. At `FEE_PERCENT = 0` this is the answer that has
        // always been here; change the constant and it stops claiming zero
        // rather than quietly contradicting the ledger it describes.
        a: FEE_PERCENT === 0
            ? 'Nothing. Nothing to join, nothing monthly, and no commission on what you earn — the fee column on every ledger row reads zero. Campaign money, subscriptions, coffees and stars reach your balance at full value.'
            : `Nothing to join and nothing monthly. Commission is ${FEE_PERCENT}% — that is the fee column on every ledger row, taken before the money reaches your balance. Campaign money, subscriptions, coffees and stars all pass through it.`,
    },
    {
        q: 'How is campaign money protected?',
        a: 'The brand funds it before you start: approval locks the amount in escrow. Disputes only unlock after the agreed revision rounds, and an unreviewed delivery auto-approves in your favour.',
    },
    {
        q: 'Do I need a big audience?',
        a: 'No. Coffees, subscriptions and stars work from your first follower, and the scorer ranks on category fit, engagement and availability — not follower count alone.',
    },
    {
        q: 'When can I withdraw?',
        // Was: 'Once your withdrawable balance reaches $1,000 you can request a payout …' — the threshold figure is no longer quoted anywhere on the site.
        a: 'Whenever your balance is cleared — to a bank account or PayPal. Requested payouts stay visible until they are marked paid.',
    },
];

/* ────────────────  the marketplace, as it actually is  ───────────────── */

/**
 * Unlike everything above this line, none of the three rows below is an
 * illustration. Every entry was read from the live, public
 * `POST /api/profile_data/`, and every card links to the page it was read
 * from — a visitor can open any of them in a new tab and check the figure
 * against the profile it came off.
 *
 * It is a dated snapshot rather than a fetch on mount, for two reasons. The
 * landing page is the first request a visitor makes, and these rows would
 * render empty until a dozen round-trips came back; and `profile_data` answers
 * one username per request, so doing it live means either forty requests from
 * the landing page or a new public endpoint to batch them. A marquee that pops
 * in three seconds late — or empties out when the API is down — is worse than
 * one that is honest about being a snapshot. `MARKET_CHECKED` is printed under
 * the rows for exactly the reason `COMPARISON_CHECKED` is: so the reader knows
 * how fresh this is, and so we know when to re-read it.
 *
 * Re-reading it is a loop over the usernames against `/api/profile_data/`.
 * Nothing here was typed by hand, and nothing here should be.
 *
 * The follower counts are the ones each account publishes on its own profile,
 * not a figure this page worked out, and the line under the rows says so.
 */
const MARKET_CHECKED = 'August 2026';

/** Totals over the **whole** published catalogue — all 35 brand accounts and
 *  the 70 campaigns they have on the board — not the 20 and the 12 the rows
 *  below have room for. The rows are a sample; these are the count. */
const MARKET_TOTALS = { brands: 35, campaigns: 70, budget: 13300, slots: 491 };

/** The 20 largest brand accounts by published audience. */
const MARKET_BRANDS = [
    { u: 'Karaca', name: 'Karaca', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/b1d1358b-5337-4f56-9f98-050860d57b00/public', followers: 2885849, tag: 'home', campaigns: 2 },
    { u: 'Owala', name: 'Owala', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/5847b385-3b45-46f6-b9f4-5e4e6be7ff00/public', followers: 1813900, tag: 'everyday', campaigns: 2 },
    { u: 'Refy', name: 'REFY Beauty', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/6ebac0c8-d8b2-49e9-9e0f-22a5f43bbb00/public', followers: 1000000, tag: 'makeup', campaigns: 2 },
    { u: 'OurPlace', name: 'Our Place', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/e8ade972-aba3-4bd6-13b0-7c3a292ee900/public', followers: 993000, tag: 'home', campaigns: 2 },
    { u: 'Manucurist', name: 'Manucurist', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/095ec014-6c36-4bc5-a29c-d159cd1f9800/public', followers: 985365, tag: 'manicure', campaigns: 2 },
    { u: 'fiTracking', name: 'Fi', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/f08961d0-22e5-4297-c6bd-7dee96382b00/public', followers: 827000, tag: 'gadgets', campaigns: 2 },
    { u: 'Saie', name: 'Saie', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/ece8c80d-c506-4c0c-75fa-528d8f45cc00/public', followers: 771000, tag: 'makeup', campaigns: 2 },
    { u: 'OliveAndJune', name: 'Olive & June', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/25a6c7ac-6c27-4291-c99f-a693cef21c00/public', followers: 751000, tag: 'manicure', campaigns: 2 },
    { u: 'Missoma', name: 'Missoma', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/d5945efc-63ac-4f33-b3e8-a58b20e1c700/public', followers: 662550, tag: 'fashion', campaigns: 2 },
    { u: 'TrinnyLondon', name: 'Trinny London', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/a341b5d6-61f0-4518-0bf3-10aeb5b8c700/public', followers: 651000, tag: 'makeup', campaigns: 2 },
    { u: 'Skillshare', name: 'Skillshare', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/9dd5cee3-f4a7-4921-e03a-89ebe15edf00/public', followers: 629670, tag: 'online courses', campaigns: 2 },
    { u: 'Tower28Beauty', name: 'Tower 28 Beauty', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/f6a30ea8-95bd-425f-9b6a-cea16bcca700/public', followers: 582000, tag: 'skincare', campaigns: 2 },
    { u: 'OLIPOP', name: 'OLIPOP', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/fe0d8ea6-ab23-4c85-9f39-d6a20a1d1800/public', followers: 470000, tag: 'wellness', campaigns: 2 },
    { u: 'Bellroy', name: 'Bellroy', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/73f19986-8466-44df-d976-2d0425b96f00/public', followers: 468400, tag: 'wallets', campaigns: 2 },
    { u: 'Babbel', name: 'Babbel', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/04e1c5b5-9fec-46ea-8525-702b3d79d600/public', followers: 463000, tag: 'language learning', campaigns: 2 },
    { u: 'PairEyewear', name: 'Pair Eyewear', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/42ccb2dd-8ecf-4933-7cab-04891dc58200/public', followers: 421000, tag: 'accessories', campaigns: 2 },
    { u: 'ColorfulStandard', name: 'Colorful Standard', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/9548350b-f330-4fe6-e170-886876a28e00/public', followers: 391000, tag: 'essentials', campaigns: 2 },
    { u: 'PeakDesign', name: 'Peak Design', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/e9c756a5-3d08-4d62-52e1-9a4253e72f00/public', followers: 389000, tag: 'photography', campaigns: 2 },
    { u: 'Izipizi', name: 'IZIPIZI', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/f15cfa7c-b8e8-48c3-f518-28a5c29aaf00/public', followers: 374000, tag: 'accessories', campaigns: 2 },
    { u: 'Kotn', name: 'Kotn', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/33cfa658-be43-4335-b788-7de36e8f9d00/public', followers: 298000, tag: 'sustainable', campaigns: 2 },
];

/** The largest open brief from each of twelve different brands, so the row
 *  reads as twelve brands hiring rather than one brand posting twelve times. */
const MARKET_CAMPAIGNS = [
    {
        brand: 'Babbel', u: 'Babbel', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/04e1c5b5-9fec-46ea-8525-702b3d79d600/public',
        title: '15 minutes a day, speaking in three months',
        budget: 600, model: 'hybrid', commission: 0, barter: true,
        platforms: ['youtube', 'instagram', 'tiktok'], slots: 3,
    },
    {
        brand: 'ASKET', u: 'Asket', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/694bae65-86cb-4e7b-291d-939753d98400/public',
        title: 'The 10-piece wardrobe challenge',
        budget: 400, model: 'fixed', commission: 0, barter: true,
        platforms: ['instagram', 'tiktok', 'youtube'], slots: 2,
    },
    {
        brand: 'Olive & June', u: 'OliveAndJune', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/25a6c7ac-6c27-4291-c99f-a693cef21c00/public',
        title: 'Gel that lasts three weeks, no nail salon',
        budget: 350, model: 'fixed', commission: 0, barter: true,
        platforms: ['tiktok', 'instagram', 'youtube'], slots: 3,
    },
    {
        brand: 'Ombraz', u: 'Ombraz', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/9044d9d9-49fa-48d2-19ad-193d130a4700/public',
        title: 'Three days in the mountains on Zeiss glass',
        budget: 350, model: 'fixed', commission: 0, barter: true,
        platforms: ['youtube'], slots: 2,
    },
    {
        brand: 'Skillshare', u: 'Skillshare', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/9dd5cee3-f4a7-4921-e03a-89ebe15edf00/public',
        title: 'A guide to putting AI into your work',
        budget: 350, model: 'fixed', commission: 0, barter: true,
        platforms: ['youtube', 'linkedin', 'tiktok', 'instagram'], slots: 4,
    },
    {
        brand: 'Our Place', u: 'OurPlace', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/e8ade972-aba3-4bd6-13b0-7c3a292ee900/public',
        title: 'Six appliances that don’t take up the counter',
        budget: 350, model: 'hybrid', commission: 10, barter: true,
        platforms: ['instagram', 'tiktok', 'youtube', 'pinterest'], slots: 4,
    },
    {
        brand: 'Peak Design', u: 'PeakDesign', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/e9c756a5-3d08-4d62-52e1-9a4253e72f00/public',
        title: 'A tripod the width of a water bottle',
        budget: 300, model: 'fixed', commission: 0, barter: true,
        platforms: ['youtube', 'instagram', 'tiktok'], slots: 3,
    },
    {
        brand: 'MacPaw', u: 'MacPaw', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/4e5e6b19-35f9-499f-b808-a010b2d02700/public',
        title: '240 apps, one subscription',
        budget: 300, model: 'hybrid', commission: 0, barter: true,
        platforms: ['youtube', 'x', 'linkedin', 'tiktok'], slots: 5,
    },
    {
        brand: 'Fi', u: 'fiTracking', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/f08961d0-22e5-4297-c6bd-7dee96382b00/public',
        title: 'How long would it take to find your dog?',
        budget: 300, model: 'hybrid', commission: 0, barter: true,
        platforms: ['tiktok', 'instagram', 'youtube'], slots: 5,
    },
    {
        brand: 'NOMATIC', u: 'Nomatic', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/f72fd686-008f-48b5-3858-c10e313fd500/public',
        title: 'A week with no checked bag',
        budget: 300, model: 'hybrid', commission: 10, barter: true,
        platforms: ['youtube', 'instagram', 'tiktok'], slots: 5,
    },
    {
        brand: 'Keychron', u: 'Keychron', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/6508b140-44a0-4545-0c92-5605a3cc7400/public',
        title: 'The keyboard that splits in two for wrist pain',
        budget: 280, model: 'fixed', commission: 0, barter: true,
        platforms: ['youtube', 'tiktok', 'linkedin'], slots: 3,
    },
    {
        brand: 'Half Magic Beauty', u: 'HalfMagic', pp: 'https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ/5ce9a471-5af9-4123-28f9-a0688cc74200/public',
        title: 'Recreate the Euphoria look yourself',
        budget: 250, model: 'hybrid', commission: 10, barter: true,
        platforms: ['tiktok', 'instagram', 'youtube'], slots: 4,
    },
];

/** Every creator carrying both a photo and a published audience — the whole
 *  set, not a flattering pick out of it. An account that has not filled its
 *  profile in is simply not shown, and nobody is invented to pad the row. */
const MARKET_CREATORS = [
    {
        u: 'Adamthedunker', pp: 'https://partnerli.com/media/profile_images/964797cb-466b-4d19-9704-90e038b2826a.jpeg', verified: true, followers: 4804691,
        platforms: [{ p: 'tiktok', f: 2100000 }, { p: 'youtube', f: 1960000 }, { p: 'instagram', f: 744691 }],
        tags: ['Fitness', 'Sport'],
    },
    {
        u: 'Jeanae_Laine', pp: 'https://partnerli.com/media/profile_images/7eb72962-e985-4bd9-8f32-19e989ea591d.jpeg', verified: false, followers: 154160,
        platforms: [{ p: 'tiktok', f: 145600 }, { p: 'youtube', f: 8560 }],
        tags: ['Humor', 'General'],
    },
    {
        u: 'Lucas', pp: 'https://partnerli.com/media/profile_images/65a1f110-f776-4219-8e5e-b853864dff76.jpeg', verified: false, followers: 89417,
        platforms: [{ p: 'instagram', f: 46257 }, { p: 'tiktok', f: 33800 }, { p: 'youtube', f: 9360 }],
        tags: ['Fun', 'Reel'],
    },
    {
        u: 'Ivanise_Benejan', pp: 'https://partnerli.com/media/profile_images/a2c18171-acf7-48d2-85c7-9dc48d50b656.jpeg', verified: false, followers: 71917,
        platforms: [{ p: 'tiktok', f: 71200 }, { p: 'instagram', f: 717 }],
        tags: ['Hair care', 'Beauty'],
    },
    {
        u: 'Maksym_Turek', pp: 'https://partnerli.com/media/profile_images/d9f6b3c2-6370-40d3-8c59-3c1722131cd0.jpeg', verified: true, followers: 20658,
        platforms: [{ p: 'youtube', f: 13100 }, { p: 'tiktok', f: 6079 }, { p: 'instagram', f: 1479 }],
        tags: [],
    },
    {
        u: 'Samantha_Andrews', pp: 'https://partnerli.com/media/profile_images/299c0a78-ef40-4785-9092-bc480e15b579.jpeg', verified: false, followers: 18200,
        platforms: [{ p: 'tiktok', f: 13000 }, { p: 'facebook', f: 5200 }],
        tags: ['Wellness', 'Life'],
    },
    {
        u: 'Serena_Barragan', pp: 'https://partnerli.com/media/profile_images/b804aedb-a169-4c11-9777-2df9aa9770ce.jpeg', verified: false, followers: 17900,
        platforms: [{ p: 'tiktok', f: 17900 }],
        tags: ['General'],
    },
    {
        u: 'Toby_Abbott', pp: 'https://partnerli.com/media/profile_images/680236fc-8477-4558-aec1-8ac5ef06c1b2.jpeg', verified: false, followers: 12870,
        platforms: [{ p: 'instagram', f: 6600 }, { p: 'tiktok', f: 6100 }, { p: 'youtube', f: 170 }],
        tags: ['Street Interviews', 'Interviews'],
    },
    {
        u: 'ROLLINGROCKSTV', pp: 'https://partnerli.com/media/profile_images/7def9507-01c0-4fd7-81b3-b2a29eea4c80.jpeg', verified: false, followers: 4050,
        platforms: [{ p: 'tiktok', f: 3300 }, { p: 'instagram', f: 750 }],
        tags: ['Humor', 'Skit'],
    },
];

/* ─────────────────────────────  utilities  ─────────────────────────────── */

const usd = (value, decimals = 2) => `$${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: decimals, maximumFractionDigits: decimals,
})}`;

const usdShort = (value) => {
    const n = Number(value || 0);
    return n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${n.toFixed(0)}`;
};

/** Follower counts, at the width a card has for them: `2.9M`, `582K`, `4,050`.
 *  The decimal is dropped once it stops carrying information — `18M` rather
 *  than `17.7M`, `154K` rather than `154.2K` — so a row of these reads as a
 *  column of numbers instead of a row of noise. */
const compact = (value) => {
    const n = Number(value || 0);
    if (n >= 1e6) return `${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)}M`;
    if (n >= 1e3) return `${(n / 1e3).toFixed(n >= 1e5 ? 0 : 1)}K`;
    return n.toLocaleString('en-US');
};

/** The cash half of a brief, which is the half that goes in the loud pill. */
const payLabel = (campaign) => {
    if (campaign.model === 'barter') return 'Product only';
    if (campaign.model === 'affiliate') return `${campaign.commission}% of sales`;
    return usd(campaign.budget, 0);
};

/** And the rest of the offer, spelled out rather than shown as a bare
 *  percentage. This page's headline number is a 0% commission, and a `+ 10%`
 *  sitting next to a budget reads like one being *taken* rather than one being
 *  paid to the creator on top of the fee. */
const payExtras = (campaign) => [
    campaign.model !== 'affiliate' && campaign.commission ? `${campaign.commission}% of sales` : null,
    campaign.barter ? 'product' : null,
].filter(Boolean).join(' · ');

const TOTALS = BREAKDOWN.reduce((acc, row) => ({
    gross: acc.gross + row.gross, fee: acc.fee + row.fee, net: acc.net + row.net,
}), { gross: 0, fee: 0, net: 0 });

/** Fade-up on entry. Falls back to "already visible" without an observer or
 *  when the reader has asked for less motion — the page must never depend on
 *  an animation having run to be readable. */
function Reveal({ children, delay = 0, className = '' }) {
    const ref = useRef(null);
    const [shown, setShown] = useState(false);

    useEffect(() => {
        const node = ref.current;
        if (!node) return undefined;
        const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        if (reduced || typeof IntersectionObserver === 'undefined') { setShown(true); return undefined; }

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) { setShown(true); observer.disconnect(); }
        }, { threshold: 0.08, rootMargin: '0px 0px -6% 0px' });
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={ref} className={className}
            style={{
                opacity: shown ? 1 : 0,
                transform: shown ? 'none' : 'translateY(22px)',
                transition: `opacity .6s cubic-bezier(.22,.68,0,1) ${delay}ms, transform .6s cubic-bezier(.22,.68,0,1) ${delay}ms`,
            }}>
            {children}
        </div>
    );
}

/** Underscores and dots split like spaces do, so a username standing in for a
 *  name — `Jeanae_Laine` — still gets two initials rather than one. */
function Avatar({ name, gradient, size = 44, className = '' }) {
    const initials = name.split(/[\s_.]+/).filter(Boolean)
        .map((word) => word[0]).join('').slice(0, 2).toUpperCase();
    return (
        <span
            aria-hidden="true"
            className={`inline-flex items-center justify-center rounded-full text-white font-black flex-shrink-0 ${className}`}
            style={{ width: size, height: size, background: gradient, fontSize: size * 0.36 }}
        >
            {initials}
        </span>
    );
}

function Icon({ path, className = 'w-5 h-5', strokeWidth = 2 }) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none"
            viewBox="0 0 24 24" stroke="currentColor" strokeWidth={strokeWidth} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d={path} />
        </svg>
    );
}

const PATHS = {
    check: 'M5 13l4 4L19 7',
    shield: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-.34-.014-.677-.04-1.016z',
    doc: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    clock: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    starOutline: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z',
    users: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
    link: 'M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5m6.5-6.5l1.5-1.5a4 4 0 115.656 5.656l-3 3a4 4 0 01-5.656 0',
    bolt: 'M13 10V3L4 14h7v7l9-11h-7z',
    chat: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4-.8L3 20l1.3-3.9A7.96 7.96 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
    trend: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
    briefcase: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
    arrow: 'M17 8l4 4m0 0l-4 4m4-4H3',
    lock: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
    sparkle: 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z',
};

/* ───────────────────────────  shared pieces  ──────────────────────────── */

function Wordmark({ dark = false, size = 'text-xl' }) {
    return (
        <span className="inline-flex items-center gap-2 select-none">
            {/* The real app icon (`public/logo512.png`), not a drawn stand-in —
                the header and the footer share this one mark, so both change here. */}
            <img src="/logo512.png" alt="" aria-hidden="true"
                className="w-8 h-8 rounded-xl object-cover shadow-[0_6px_18px_-6px_rgba(32,201,181,0.9)]" />
            <span className={`${size} font-black tracking-tight ${dark ? 'text-white' : 'text-slate-900'}`}>
                Astreona
            </span>
        </span>
    );
}

/** A browser chrome around the product mocks. Without a frame a screenshot of
 *  a card reads as decoration; inside one it reads as the product. */
function Frame({ url, children, className = '' }) {
    return (
        <div className={`rounded-2xl sm:rounded-[26px] bg-white border border-slate-200 shadow-[0_30px_80px_-40px_rgba(15,23,42,0.45)] overflow-hidden ${className}`}>
            <div className="flex items-center gap-2 px-3.5 py-2.5 bg-slate-100 border-b border-slate-200">
                <span className="flex gap-1.5 flex-shrink-0">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
                </span>
                <span className="flex-1 min-w-0 mx-1 px-3 py-1 rounded-md bg-white border border-slate-200 text-[11px] text-slate-500 font-medium truncate">
                    {url}
                </span>
                <span className="hidden sm:inline text-[10px] font-bold text-slate-400 uppercase tracking-wider flex-shrink-0">
                    example account
                </span>
            </div>
            <div className="bg-slate-50">{children}</div>
        </div>
    );
}

/**
 * A phone. Narrow and thin-bezelled on purpose: it is the proof, not the hero,
 * and a fat plastic frame reads as a 2014 mockup. The rail is a gradient rather
 * than a flat fill — one hairline of light along the left edge is what makes
 * 3px of border look like machined metal.
 *
 * One definition, two mockups (the link strip's profile page and the app band's
 * lock screen), so the device cannot end up drawn two different ways on one
 * page. The width is an inline style rather than a Tailwind class because a
 * class built from a prop is exactly what the JIT compiler cannot see.
 */
function PhoneShell({ width = 196, frameRef, className = '', screenClassName = 'bg-white', children }) {
    return (
        <div ref={frameRef}
            className={`relative rounded-[36px] p-[3px] shadow-[0_34px_64px_-30px_rgba(15,23,42,0.8)] ${className}`}
            style={{ width, background: 'linear-gradient(150deg,#8b97a8 0%,#2b3646 26%,#0b111c 58%,#39445580 78%,#141c28 100%)' }}>
            {/* Side buttons, on the rail itself. */}
            <span className="absolute -left-[1.5px] top-[86px] w-[2px] h-[22px] rounded-l-sm bg-slate-600/80" aria-hidden="true" />
            <span className="absolute -left-[1.5px] top-[114px] w-[2px] h-[22px] rounded-l-sm bg-slate-600/80" aria-hidden="true" />
            <span className="absolute -right-[1.5px] top-[100px] w-[2px] h-[34px] rounded-r-sm bg-slate-600/80" aria-hidden="true" />

            <div className={`relative rounded-[33px] overflow-hidden ${screenClassName}`}>
                {/* Dynamic Island: one pill, floating over the content rather
                    than notched out of it, with the lens sitting inside its
                    right end. */}
                <span className="absolute top-[7px] left-1/2 -translate-x-1/2 z-30 h-[15px] w-[52px] rounded-full bg-black
                        flex items-center justify-end pr-[4px] shadow-[0_1px_3px_rgba(0,0,0,0.45)]"
                    aria-hidden="true">
                    <span className="w-[7px] h-[7px] rounded-full"
                        style={{ background: 'radial-gradient(circle at 32% 30%, #1e3a5f 0%, #050b14 62%)' }} />
                </span>

                {children}
            </div>
        </div>
    );
}

function SectionHead({ eyebrow, title, desc, dark = false, center = true }) {
    return (
        <div className={`${center ? 'text-center mx-auto' : ''} max-w-2xl mb-10 sm:mb-14`}>
            {eyebrow && (
                <span className={`inline-block text-[11px] font-black uppercase tracking-[0.18em] mb-3
                    ${dark ? 'text-primary-300' : 'text-primary-700'}`}>
                    {eyebrow}
                </span>
            )}
            <h2 className={`text-[28px] sm:text-4xl font-black tracking-tight leading-[1.12]
                ${dark ? 'text-white' : 'text-slate-900'}`}>
                {title}
            </h2>
            {desc && (
                <p className={`mt-3.5 text-[15px] sm:text-base leading-relaxed ${dark ? 'text-slate-300' : 'text-slate-600'}`}>
                    {desc}
                </p>
            )}
        </div>
    );
}

/* ────────────────────────────────  nav  ──────────────────────────────── */

function TopNav({ onLogin, onSignup }) {
    const [solid, setSolid] = useState(false);
    const [menu, setMenu] = useState(false);

    useEffect(() => {
        const onScroll = () => setSolid(window.scrollY > 24);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const links = [
        { href: '#compare', label: 'Compare' },
        { href: '#earn', label: 'Ways to earn' },
        { href: '#marketplace', label: 'Marketplace' },
        { href: '#product', label: 'The product' },
        // { href: '#trust', label: 'How you get paid' },
        { href: '#faq', label: 'FAQ' },
    ];

    return (
        <header className={`fixed top-0 inset-x-0 z-40 transition-all duration-300
            ${solid ? 'bg-white/85 backdrop-blur-xl border-b border-slate-200/80 shadow-[0_4px_24px_-16px_rgba(15,23,42,0.5)]' : 'bg-transparent'}`}>
            <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-6">
                <a href="#top" className="no-underline flex-shrink-0">
                    <Wordmark />
                </a>

                <nav className="hidden lg:flex items-center gap-7 ml-4">
                    {links.map((link) => (
                        <a key={link.href} href={link.href}
                            className="text-sm font-semibold no-underline text-slate-600 hover:text-primary-700 transition">
                            {link.label}
                        </a>
                    ))}
                </nav>

                <div className="ml-auto flex items-center gap-2 sm:gap-3">
                    <button type="button" onClick={onLogin}
                        className="px-3.5 sm:px-4 py-2 rounded-full text-sm font-bold text-slate-700 hover:bg-slate-100 transition">
                        Log in
                    </button>
                    <button type="button" onClick={onSignup}
                        className="px-4 sm:px-5 py-2 rounded-full text-sm font-bold text-white
                            bg-gradient-to-r from-primary-500 to-primary-700
                            shadow-[0_8px_22px_-8px_rgba(32,201,181,0.9)] hover:-translate-y-px active:translate-y-0 transition">
                        Sign up
                    </button>
                    <button type="button" onClick={() => setMenu((open) => !open)}
                        aria-label="Menu" aria-expanded={menu}
                        className="lg:hidden w-9 h-9 rounded-full flex items-center justify-center text-slate-700 hover:bg-slate-100 transition">
                        <Icon path={menu ? 'M6 18L18 6M6 6l12 12' : 'M4 7h16M4 12h16M4 17h16'} />
                    </button>
                </div>
            </div>

            {menu && (
                <div className="lg:hidden bg-white border-t border-slate-100 shadow-lg px-4 py-3 animate-fade-in">
                    {links.map((link) => (
                        <a key={link.href} href={link.href} onClick={() => setMenu(false)}
                            className="block py-2.5 text-sm font-semibold text-slate-700 no-underline border-b border-slate-50 last:border-0">
                            {link.label}
                        </a>
                    ))}
                </div>
            )}
        </header>
    );
}

/* ───────────────────────────────  hero  ──────────────────────────────── */

function HeroBalanceCard() {
    const progressLabel = `${Math.round((BALANCE.available / (BALANCE.available + BALANCE.pending)) * 100)}%`;
    return (
        <div className="rounded-3xl overflow-hidden bg-gradient-to-br from-primary-600 to-primary-800 text-white p-5 shadow-[0_30px_70px_-30px_rgba(0,0,0,0.75)] border border-white/10">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <p className="text-[11px] font-bold text-white/70 uppercase tracking-wider">Withdrawable balance</p>
                    <p className="text-4xl sm:text-[42px] font-black mt-1 tracking-tight tabular-nums">{usd(BALANCE.available)}</p>
                </div>
                <span className="px-2.5 py-1 rounded-full bg-white/15 border border-white/20 text-[10px] font-black uppercase tracking-wider flex-shrink-0">
                    Ready
                </span>
            </div>

            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-[11px] text-white/80">
                <span>Pending <strong className="text-white tabular-nums">{usd(BALANCE.pending)}</strong></span>
                <span>Lifetime <strong className="text-white tabular-nums">{usd(BALANCE.lifetimeNet)}</strong></span>
                <span>Withdrawn <strong className="text-white tabular-nums">{usd(BALANCE.withdrawn)}</strong></span>
            </div>

            <div className="mt-4">
                <div className="flex justify-between text-[10px] text-white/70 mb-1.5">
                    <span>Cleared vs. still settling</span>
                    <span className="tabular-nums">{progressLabel} cleared</span>
                </div>
                <div className="h-2 rounded-full bg-white/20 overflow-hidden">
                    <div className="h-full rounded-full bg-white" style={{ width: progressLabel }} />
                </div>
            </div>

            <button type="button" tabIndex={-1}
                className="mt-4 w-full py-2.5 rounded-xl bg-white text-primary-800 font-black text-sm cursor-default">
                Request payout
            </button>
            {/* <p className="text-[10px] text-white/60 text-center mt-2">Minimum payout $1,000 · bank transfer or PayPal</p> */}
            <p className="text-[10px] text-white/60 text-center mt-2">Bank transfer or PayPal</p>
        </div>
    );
}

/* ───────────────────────  the hero earning stage  ─────────────────────── */

/**
 * Four people pay one creator, on a loop.
 *
 * A list of income streams is something you read; this is something you watch
 * happen, which is the difference between "there are four ways to earn" and
 * "money is arriving". Each sender fires in turn, a token flies to the middle,
 * and the creator's balance fills green as it lands.
 *
 * The loop is arithmetic, not decoration: the four net amounts added to
 * `STAGE_BASE` land on exactly `STAGE_TARGET` — the same $9,800 the dashboard
 * further down the page shows — so the animation cannot quietly disagree with
 * the screenshot of the product it is introducing. The campaign goes last
 * because it is the big one, and a balance that jumps at the end of the cycle
 * is the part worth watching.
 *
 * Amounts run through `netOf` like the balance they are filling, so the stage
 * cannot quietly disagree with the ledger below it if a fee is ever charged
 * again. With the fee at zero the two are the same figure.
 */

const STAGE_BASE = 5165.01;
const STAGE_TARGET = BALANCE.available;   // 9,800.00 — one full loop lands here
const FLIGHT_MS = 1250;   // sender → creator
const CYCLE_MS = 2100;    // gap between senders (> FLIGHT_MS, so one lands at a time)
const HOLD_MS = 2600;     // how long the completed balance sits before the loop resets

const SENDERS = [
    {
        key: 'star', name: 'Ava Bennett', action: 'sent 100 ★',
        gross: 100, at: { left: '14%', top: '15%' },
        art: <img src="/star192.png" alt="" className="w-full h-full object-contain" />,
    },
    {
        key: 'coffee', name: 'Liam Novak', action: 'bought 5 coffees',
        gross: 25, at: { left: '86%', top: '17%' },
        art: <img src="/coffee256.png" alt="" className="w-full h-full object-contain" />,
    },
    {
        key: 'subscription', name: 'Mia Torres', action: 'subscribed · $9.99/mo',
        gross: 9.99, at: { left: '13%', top: '81%' },
        art: <Icon path={PATHS.users} className="w-full h-full p-0.5" strokeWidth={2.5} />,
    },
    {
        key: 'campaign', name: 'Lumière Beauty', action: 'campaign released · $4,500',
        gross: 4500, at: { left: '87%', top: '79%' }, brand: true,
        art: <Icon path={PATHS.briefcase} className="w-full h-full p-0.5" strokeWidth={2.5} />,
    },
].map((sender) => ({ ...sender, net: netOf(sender.gross) }));

/** Counts up to a new target; snaps instantly when the loop resets downward. */
function useCountUp(target, ms = 800) {
    const [display, setDisplay] = useState(target);
    const fromRef = useRef(target);

    useEffect(() => {
        const from = fromRef.current;
        if (from === target) return undefined;
        if (target < from || typeof requestAnimationFrame === 'undefined') {
            fromRef.current = target;
            setDisplay(target);
            return undefined;
        }
        let frame;
        const started = Date.now();
        const step = () => {
            const progress = Math.min(1, (Date.now() - started) / ms);
            const eased = 1 - Math.pow(1 - progress, 3);
            setDisplay(from + (target - from) * eased);
            if (progress < 1) { frame = requestAnimationFrame(step); }
            else { fromRef.current = target; }
        };
        frame = requestAnimationFrame(step);
        return () => { fromRef.current = target; cancelAnimationFrame(frame); };
    }, [target, ms]);

    return display;
}

function SenderNode({ sender, firing }) {
    const tone = SERIES_COLORS[sender.key];
    return (
        <div className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1.5 w-[92px]"
            style={{ left: sender.at.left, top: sender.at.top }}>
            <span className={`relative w-[52px] h-[52px] rounded-full bg-white flex items-center justify-center
                    shadow-[0_8px_22px_-10px_rgba(15,23,42,0.5)] transition-transform duration-300
                    ${firing ? 'scale-110' : 'scale-100'}`}
                style={{ border: `2px solid ${firing ? tone : '#e2e8f0'}` }}>
                {firing && (
                    <span className="absolute inset-0 rounded-full astreona-ping"
                        style={{ border: `2px solid ${tone}` }} aria-hidden="true" />
                )}
                <span className="w-7 h-7 text-slate-500 flex items-center justify-center" aria-hidden="true">
                    {sender.brand
                        ? <Icon path={PATHS.briefcase} className="w-6 h-6 text-slate-600" />
                        : <Icon path="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" className="w-6 h-6" strokeWidth={1.6} />}
                </span>
                {/* What this person sends, on the corner of who they are. */}
                <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white border border-slate-200 p-1 flex items-center justify-center shadow-sm"
                    style={{ color: tone }}>
                    {sender.art}
                </span>
            </span>
            <span className="text-[10.5px] font-bold text-slate-600 leading-tight text-center truncate w-full">
                {sender.name}
            </span>
        </div>
    );
}

function EarningStage() {
    // jsdom has no matchMedia; treating that as "reduced motion" keeps tests
    // deterministic and changes nothing in a real browser, which always has it.
    const still = typeof window === 'undefined'
        || typeof window.matchMedia !== 'function'
        || window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const [balance, setBalance] = useState(still ? STAGE_TARGET : STAGE_BASE);
    const [flights, setFlights] = useState([]);
    const [pops, setPops] = useState([]);
    const [firing, setFiring] = useState(null);

    useEffect(() => {
        if (still) return undefined;
        const timers = new Set();
        const later = (fn, ms) => {
            const id = setTimeout(() => { timers.delete(id); fn(); }, ms);
            timers.add(id);
            return id;
        };

        // Self-scheduling rather than a fixed interval: the loop has to pause
        // on the last arrival long enough to show the full balance and reset
        // before the next sender fires. On an interval the reset lands in the
        // middle of the following cycle and the running total stops adding up
        // to STAGE_TARGET — which is the one thing this animation must do.
        let step = 0;
        const send = () => {
            const sender = SENDERS[step % SENDERS.length];
            const last = step % SENDERS.length === SENDERS.length - 1;
            const id = `${sender.key}-${step}`;
            step += 1;

            setFiring(sender.key);
            setFlights((current) => [...current, { id, sender }]);
            later(() => setFiring((key) => (key === sender.key ? null : key)), 600);

            later(() => {
                setFlights((current) => current.filter((flight) => flight.id !== id));
                setPops((current) => [...current, { id, sender }]);
                setBalance((value) => Math.round((value + sender.net) * 100) / 100);
                later(() => setPops((current) => current.filter((pop) => pop.id !== id)), 1300);
                if (last) later(() => setBalance(STAGE_BASE), HOLD_MS);
            }, FLIGHT_MS);

            later(send, last ? FLIGHT_MS + HOLD_MS + 500 : CYCLE_MS);
        };

        later(send, 700);
        return () => timers.forEach(clearTimeout);
    }, [still]);

    const shown = useCountUp(balance);
    const filled = Math.max(0, Math.min(1, (shown - STAGE_BASE) / (STAGE_TARGET - STAGE_BASE)));
    const ring = 2 * Math.PI * 46;

    return (
        <div className="relative w-full max-w-[460px] mx-auto aspect-square min-h-[320px]">
            {/* Flow lines. Non-scaling strokes so the square-ish viewBox cannot
                stretch them into wedges. */}
            <svg viewBox="0 0 100 100" preserveAspectRatio="none"
                className="absolute inset-0 w-full h-full" aria-hidden="true">
                {SENDERS.map((sender) => (
                    <line key={sender.key}
                        x1={parseFloat(sender.at.left)} y1={parseFloat(sender.at.top)} x2="50" y2="50"
                        stroke={firing === sender.key ? SERIES_COLORS[sender.key] : '#cbd5e1'}
                        strokeWidth="1.4" strokeDasharray="3 4" strokeLinecap="round"
                        vectorEffect="non-scaling-stroke"
                        className="astreona-flow"
                        opacity={firing === sender.key ? 0.9 : 0.45} />
                ))}
            </svg>

            {SENDERS.map((sender) => (
                <SenderNode key={sender.key} sender={sender} firing={firing === sender.key} />
            ))}

            {/* In flight */}
            {flights.map((flight) => (
                <Flight key={flight.id} sender={flight.sender} />
            ))}

            {/* The creator. The avatar is centred on the stage's exact middle
                because that is where the flow lines and the flight tokens
                converge; the readout hangs below it as its own absolute block
                rather than sharing a centred column, which would push the
                avatar up off the convergence point by half the text height. */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                <div className="relative">
                    <svg width="112" height="112" viewBox="0 0 112 112" className="-rotate-90" aria-hidden="true">
                        <circle cx="56" cy="56" r="46" fill="none" stroke="#e2e8f0" strokeWidth="6" />
                        <circle cx="56" cy="56" r="46" fill="none" stroke="url(#astreonaFill)" strokeWidth="6"
                            strokeLinecap="round" strokeDasharray={ring}
                            strokeDashoffset={ring * (1 - filled)}
                            style={{ transition: 'stroke-dashoffset .8s cubic-bezier(.22,.68,0,1)' }} />
                        <defs>
                            <linearGradient id="astreonaFill" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor="#34d399" />
                                <stop offset="100%" stopColor="#0f8a7e" />
                            </linearGradient>
                        </defs>
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center">
                        <Avatar name={CREATOR.name} gradient={CREATOR.gradient} size={86}
                            className="shadow-[0_10px_30px_-12px_rgba(15,23,42,0.6)]" />
                    </span>

                    {/* Arrivals, floating off the top of the avatar */}
                    {pops.map((pop) => (
                        <span key={pop.id}
                            className="absolute left-1/2 -top-1 -translate-x-1/2 whitespace-nowrap px-2.5 py-1 rounded-full
                                bg-emerald-500 text-white text-[12px] font-black tabular-nums shadow-lg astreona-rise">
                            +{usd(pop.sender.net)}
                        </span>
                    ))}
                </div>
            </div>

            <div className="absolute left-1/2 -translate-x-1/2 top-[calc(50%+62px)] text-center whitespace-nowrap">
                <p className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400 m-0">
                    {CREATOR.name.split(' ')[0]}’s balance
                </p>
                <p className="text-[26px] sm:text-[30px] font-black text-emerald-600 tabular-nums leading-none mt-1 m-0">
                    {usd(shown)}
                </p>
            </div>
        </div>
    );
}

/** One token, mounted at its sender and moved to the middle on the next frame
 *  so the browser has a start position to transition away from. */
function Flight({ sender }) {
    const [arrived, setArrived] = useState(false);

    useEffect(() => {
        const frame = requestAnimationFrame(() => requestAnimationFrame(() => setArrived(true)));
        return () => cancelAnimationFrame(frame);
    }, []);

    const tone = SERIES_COLORS[sender.key];

    return (
        <span
            className="absolute z-10 -translate-x-1/2 -translate-y-1/2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full
                bg-white shadow-[0_10px_26px_-10px_rgba(15,23,42,0.6)] pointer-events-none"
            style={{
                left: arrived ? '50%' : sender.at.left,
                top: arrived ? '50%' : sender.at.top,
                border: `1.5px solid ${tone}`,
                opacity: arrived ? 0.15 : 1,
                transform: `translate(-50%,-50%) scale(${arrived ? 0.55 : 1})`,
                transition: `left ${FLIGHT_MS}ms cubic-bezier(.45,0,.25,1), top ${FLIGHT_MS}ms cubic-bezier(.45,0,.25,1),
                    transform ${FLIGHT_MS}ms cubic-bezier(.45,0,.25,1), opacity ${FLIGHT_MS}ms ease-in`,
            }}
        >
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0" style={{ color: tone }}>
                {sender.art}
            </span>
            <span className="text-[11px] font-black tabular-nums" style={{ color: tone }}>
                +{usd(sender.net)}
            </span>
        </span>
    );
}

function Hero({ onSignup, onLogin }) {
    return (
        <section id="top" className="relative overflow-hidden astreona-hero">
            <div className="astreona-hero-glow" aria-hidden="true" />
            <div className="astreona-hero-grid" aria-hidden="true" />

            <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-28 sm:pt-36 pb-20 sm:pb-28">
                <div className="grid lg:grid-cols-[1.05fr,0.95fr] gap-12 lg:gap-10 items-center">

                    <div>
                        {/* <Reveal>
                            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-primary-200 shadow-sm text-[11px] font-bold text-primary-700 uppercase tracking-[0.14em]">
                                <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse" />
                                The one platform for monetising your work
                            </span>
                        </Reveal> */}

                        <Reveal delay={60}>
                            <h1 className="mt-6 text-[38px] sm:text-6xl font-black tracking-tight leading-[1.04] text-slate-900">
                                One profile.
                                <br className="hidden sm:block" />
                                <span className="astreona-gradient-ink"> Four ways to get paid.</span>
                            </h1>
                        </Reveal>

                        <Reveal delay={120}>
                            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
                                Brand campaigns, subscriptions, coffees and star gifts —
                                on one page, in one balance, out in one payout.
                            </p>
                        </Reveal>

                        {/* The fee is the first objection and the strongest
                            answer, so it is a claim of its own rather than the
                            third item in a row of ticks — and now that the
                            answer is zero it carries the brand gradient rather
                            than sitting in a plain white chip. */}
                        <Reveal delay={150}>
                            <div className="mt-6 inline-flex items-center gap-3.5 rounded-2xl bg-gradient-to-r from-primary-600 to-primary-700 shadow-[0_18px_44px_-20px_rgba(32,201,181,0.95)] pl-4 pr-5 py-3">
                                <span className="flex items-baseline gap-0.5 text-white">
                                    <span className="text-[38px] font-black leading-none tracking-tight">{FEE_PERCENT}</span>
                                    <span className="text-xl font-black leading-none">%</span>
                                </span>
                                <span className="text-[12.5px] text-white/85 leading-snug">
                                    <strong className="block text-white font-black text-[14px]">Zero commission. Ever.</strong>
                                    Nothing monthly, nothing per payout. You keep 100%.
                                </span>
                            </div>
                        </Reveal>

                        <Reveal delay={190}>
                            <div className="mt-7 flex flex-col sm:flex-row gap-3">
                                <button type="button" onClick={onSignup}
                                    className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-[15px] font-black text-white
                                        bg-gradient-to-r from-primary-500 to-primary-700
                                        shadow-[0_16px_40px_-14px_rgba(32,201,181,0.95)]
                                        hover:-translate-y-0.5 active:translate-y-0 transition">
                                    Start earning free
                                    <Icon path={PATHS.arrow} className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                                </button>
                                <button type="button" onClick={onLogin}
                                    className="inline-flex items-center justify-center px-7 py-3.5 rounded-full text-[15px] font-bold
                                        text-slate-700 bg-white border border-slate-200 hover:border-primary-300 hover:text-primary-700 transition">
                                    I already have an account
                                </button>
                            </div>
                        </Reveal>

                        <Reveal delay={240}>
                            <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-slate-500">
                                {['Free to join', '0% commission', 'No monthly fee', 'Escrow-protected campaigns', 'Payouts to bank or PayPal'].map((item) => (
                                    <span key={item} className="inline-flex items-center gap-1.5">
                                        <Icon path={PATHS.check} className="w-3.5 h-3.5 text-primary-600" strokeWidth={3} />
                                        {item}
                                    </span>
                                ))}
                            </div>
                        </Reveal>
                    </div>

                    <Reveal delay={140}>
                        <div className="relative">
                            <EarningStage />
                            {/* <p className="mt-1 text-center text-[10.5px] text-slate-400 leading-relaxed">
                                Example account · amounts are what lands, with no fee taken
                            </p> */}
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

/* ────────────────────────────  platform strip  ───────────────────────── */

/**
 * The strip does two jobs. It says "every platform you are already on fits
 * here" — and, because that is the line people skim past, it *shows* the other
 * thing this profile quietly is: the link-in-bio page they are currently
 * paying someone else for.
 *
 * So the icons do not just sit there. One lifts out of the cloud at random and
 * flies across into a row on the phone. The list fills top to bottom, and once
 * full it **rotates in a fixed order** — row 0, then 1, then 2, then round
 * again — with the outgoing link fading a beat before its replacement sets
 * off. Picking the row at random instead leaves a dead gap in the middle of a
 * short list, which reads as something broken rather than as something
 * arriving; a row is therefore either occupied or visibly *being* filled.
 *
 * The flight is measured from the real DOM rects of the source icon and the
 * target slot rather than from hard-coded coordinates, so it survives the
 * layout changing under it (wrap at a different width, phone under the cloud
 * on mobile) without the tokens flying off to nowhere.
 */
const STRIP_LINKS = [
    { key: 'instagram', sub: '128K followers' },
    { key: 'tiktok', sub: '46.2K followers' },
    { key: 'youtube', sub: '10.4K subscribers' },
    { key: 'x', sub: 'Daily notes' },
    { key: 'twitch', sub: 'Live Tue & Fri' },
    { key: 'linkedin', sub: 'Work profile' },
    { key: 'spotify', sub: 'The podcast' },
    { key: 'substack', sub: 'The newsletter' },
    { key: 'pinterest', sub: 'Mood boards' },
    { key: 'discord', sub: 'Community' },
    { key: 'threads', sub: 'Threads' },
    { key: 'snapchat', sub: 'Behind the scenes' },
    { key: 'patreon', sub: 'Members only' },
    { key: 'kofi', sub: 'Tip jar' },
    { key: 'etsy', sub: 'The shop' },
];

const LINK_SLOTS = 3;
const LINK_APPROACH_MS = 820;   // cloud → alongside the phone, level with the row
const LINK_SLIDE_MS = 340;      // → into the row itself
const LINK_HOP_MS = LINK_APPROACH_MS + LINK_SLIDE_MS;
const LINK_FADE_MS = 600;       // the outgoing link, before its replacement leaves
const LINK_GAP_MS = 1100;       // between launches
const LINK_HANDOFF_MS = 90;     // token and row overlap for a few frames on arrival
// A row therefore stays put for a full lap of the other slots — around eight
// seconds, which is the reading time. No separate hold constant: the pace of
// the rotation *is* the hold.

/**
 * One icon in flight, in two legs.
 *
 * A single straight hop to the row looked fine and read badly: on the way in,
 * the token drifted across the rows *above* its target, so for a moment the
 * top link showed one platform's mark sitting over another platform's name. So
 * the first leg stops beside the phone, level with the row it is aiming at,
 * and the second slides in horizontally. Everything that overlaps a row now
 * belongs to that row.
 *
 * Mounted at its source and moved on the next frame, so the browser has a
 * start position to transition away from. The two axes get different easings
 * on the approach: equal ones draw a straight diagonal, and the mismatch is
 * what bends it into an arc.
 */
function LinkFlight({ item, from, via, to }) {
    const [phase, setPhase] = useState('start');

    useEffect(() => {
        const frame = requestAnimationFrame(() => requestAnimationFrame(() => setPhase('approach')));
        const timer = setTimeout(() => setPhase('slide'), LINK_APPROACH_MS);
        return () => { cancelAnimationFrame(frame); clearTimeout(timer); };
    }, []);

    const tone = PLATFORMS[item.key]?.color || '#20c9b5';
    const sliding = phase === 'slide';
    const at = phase === 'start' ? from : (sliding ? to : via);
    const scale = phase === 'start' ? 1 : (sliding ? 0.58 : 0.74);
    const ms = sliding ? LINK_SLIDE_MS : LINK_APPROACH_MS;
    const ease = 'cubic-bezier(.36,0,.3,1)';

    return (
        <span
            className="absolute z-20 pointer-events-none rounded-full"
            style={{
                left: at.x,
                top: at.y,
                transform: `translate(-50%,-50%) scale(${scale})`,
                boxShadow: `0 10px 26px -8px ${tone}bb`,
                transition: `left ${ms}ms ${sliding ? 'cubic-bezier(.2,.8,.3,1)' : ease},
                    top ${ms}ms ${sliding ? ease : 'cubic-bezier(.7,0,.2,1)'},
                    transform ${ms}ms ${ease}`,
            }}
            aria-hidden="true"
        >
            <PlatformIcon platform={item.key} size={38} />
        </span>
    );
}

/** One row on the phone. Empty is a dashed "add a link" placeholder, which is
 *  what makes an arriving icon read as *landing somewhere* rather than as
 *  decoration drifting across the page. */
function LinkSlot({ slot, slotRef }) {
    const item = slot && STRIP_LINKS.find((link) => link.key === slot.key);
    const meta = item ? PLATFORMS[item.key] : null;
    const filled = slot?.state === 'filled';

    return (
        <div className={`h-[34px] rounded-xl flex items-center gap-2 px-1.5 transition-all duration-300
                ${filled
                ? 'bg-white border border-slate-200 shadow-[0_6px_16px_-10px_rgba(15,23,42,0.6)]'
                : `border border-dashed ${slot ? 'border-primary-400 bg-primary-50/60' : 'border-slate-200 bg-slate-50'}`}`}
            style={{ opacity: slot?.leaving ? 0 : 1, transitionDuration: slot?.leaving ? `${LINK_FADE_MS}ms` : undefined }}>
            {/* `astreona-settle` rather than `astreona-pop`: the arriving token
                hands over to this icon, so it must be opaque on its first frame.
                Popping in from zero opacity made a landing logo read as one that
                had just vanished. Keyed by platform so the settle replays on
                every change instead of only the first. */}
            <span ref={slotRef} className="w-[22px] h-[22px] flex items-center justify-center flex-shrink-0">
                {filled
                    ? <PlatformIcon key={item.key} platform={item.key} size={22} className="astreona-settle" />
                    : <Icon path={PATHS.link} className={`w-3.5 h-3.5 ${slot ? 'text-primary-500' : 'text-slate-300'}`} />}
            </span>
            <span className="min-w-0 flex-1 leading-tight">
                {filled ? (
                    <>
                        <span className="block text-[9.5px] font-black text-slate-800 truncate">{meta?.label || item.key}</span>
                        <span className="block text-[8px] text-slate-400 truncate">{item.sub}</span>
                    </>
                ) : (
                    <span className={`block text-[9px] font-bold truncate ${slot ? 'text-primary-600' : 'text-slate-400'}`}>
                        {slot ? 'Adding…' : 'Add a link'}
                    </span>
                )}
            </span>
            {filled && <Icon path={PATHS.arrow} className="w-3 h-3 text-slate-300 flex-shrink-0" />}
        </div>
    );
}

function PlatformStrip() {
    // jsdom has no matchMedia; treating that as "reduced motion" keeps tests
    // deterministic and changes nothing in a real browser, which always has it.
    const still = typeof window === 'undefined'
        || typeof window.matchMedia !== 'function'
        || window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const stageRef = useRef(null);
    const phoneRef = useRef(null);
    const iconRefs = useRef({});
    const slotRefs = useRef([]);

    // The slots are driven from a ref and mirrored into state: the loop below
    // is one long-lived effect, so reading the current rows out of a state
    // closure would always read the row of the first render.
    const slotsRef = useRef(new Array(LINK_SLOTS).fill(null));
    const [slots, setSlots] = useState(() => (
        still
            ? STRIP_LINKS.slice(0, LINK_SLOTS).map((link) => ({ key: link.key, state: 'filled' }))
            : new Array(LINK_SLOTS).fill(null)
    ));
    const [flights, setFlights] = useState([]);
    const [launching, setLaunching] = useState(null);

    useEffect(() => {
        if (still) return undefined;
        const timers = new Set();
        const later = (fn, ms) => {
            const id = setTimeout(() => { timers.delete(id); fn(); }, ms);
            timers.add(id);
            return id;
        };
        const commit = () => setSlots([...slotsRef.current]);

        const pointOf = (node) => {
            if (!stageRef.current || !node) return null;
            const stage = stageRef.current.getBoundingClientRect();
            const rect = node.getBoundingClientRect();
            return {
                x: rect.left - stage.left + rect.width / 2,
                y: rect.top - stage.top + rect.height / 2,
            };
        };

        /** Where the approach stops: just off the phone's left rail, so the
         *  final leg travels along the target row and over nothing else. */
        const gateX = () => {
            if (!stageRef.current || !phoneRef.current) return null;
            const stage = stageRef.current.getBoundingClientRect();
            return phoneRef.current.getBoundingClientRect().left - stage.left - 15;
        };

        let seq = 0;
        let cursor = 0;                 // whose turn it is to be replaced
        const launch = () => {
            const target = cursor;

            // Nothing already on the phone is offered again, so the same mark
            // never appears twice in a three-row list.
            const taken = slotsRef.current.filter(Boolean).map((slot) => slot.key);
            const pool = STRIP_LINKS.filter((link) => !taken.includes(link.key));
            const item = pool[Math.floor(Math.random() * pool.length)];

            // Measured before the outgoing row fades — only its opacity moves,
            // so the coordinates hold. If the layout is not up yet, retry this
            // same slot rather than advancing past it and leaving a gap.
            const from = pointOf(iconRefs.current[item.key]);
            const to = pointOf(slotRefs.current[target]);
            const gate = gateX();
            if (!from || !to || gate === null) { later(launch, LINK_GAP_MS); return; }

            const via = { x: Math.min(gate, to.x - 20), y: to.y };

            cursor = (cursor + 1) % LINK_SLOTS;
            seq += 1;
            const id = `${item.key}-${seq}`;

            const send = () => {
                slotsRef.current[target] = { key: item.key, state: 'incoming' };
                commit();
                setLaunching(item.key);
                setFlights((current) => [...current, { id, item, from, via, to }]);
                later(() => setLaunching((key) => (key === item.key ? null : key)), 600);

                later(() => {
                    // Row first, token a few frames later. Dropping the token in
                    // the same commit leaves one frame with neither drawn, and
                    // that frame is what made an arriving logo look like a
                    // disappearing one. They are the same mark at the same size
                    // in the same place, so the overlap cannot be seen.
                    slotsRef.current[target] = { key: item.key, state: 'filled' };
                    commit();
                    later(() => setFlights((current) => current.filter((flight) => flight.id !== id)), LINK_HANDOFF_MS);
                }, LINK_HOP_MS);

                later(launch, LINK_HOP_MS + LINK_GAP_MS);
            };

            // A row leaves only when its replacement is on the way, and the two
            // never overlap in the same slot.
            if (slotsRef.current[target]) {
                slotsRef.current[target] = { ...slotsRef.current[target], leaving: true };
                commit();
                later(send, LINK_FADE_MS);
            } else {
                send();
            }
        };

        later(launch, 900);
        return () => timers.forEach(clearTimeout);
    }, [still]);

    return (
        <section className="bg-white border-b border-slate-100">
            <div ref={stageRef} className="relative max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
                <div className="grid lg:grid-cols-[1fr,auto] gap-10 lg:gap-12 items-center">

                    <div className="text-center lg:text-left">
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">
                            Bring every platform you are already on
                        </p>
                        <h2 className="mt-2 text-[22px] sm:text-[26px] font-black tracking-tight text-slate-900 leading-tight">
                            It is your link-in-bio too —
                            <span className="astreona-gradient-ink"> and this one pays you.</span>
                        </h2>
                        <p className="mt-2 text-[13px] text-slate-500 leading-relaxed max-w-md mx-auto lg:mx-0">
                            Drop in every account, shop and newsletter you have. 
                            {/* Same single link as
                            the link page you use today, with campaigns, subscriptions, coffees and
                            stars already on it. */}
                        </p>

                        <div className="mt-6 flex flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-4">
                            {STRIP_LINKS.map((link) => (
                                <span key={link.key}
                                    ref={(node) => { iconRefs.current[link.key] = node; }}
                                    className={`transition duration-300 ${launching === link.key
                                        ? 'opacity-100 grayscale-0 scale-110'
                                        : 'opacity-60 grayscale hover:opacity-100 hover:grayscale-0'}`}>
                                    <PlatformIcon platform={link.key} size={36} />
                                </span>
                            ))}
                            <span className="text-xs font-bold text-slate-400 px-1">+ 40 more</span>
                        </div>
                    </div>

                    <div className="justify-self-center flex-shrink-0">
                        <PhoneShell frameRef={phoneRef}>
                            <div className="pt-[30px] pb-3 px-3 text-center bg-gradient-to-b from-primary-50 to-white">
                                <Avatar name={CREATOR.name} gradient={CREATOR.gradient} size={42} className="mx-auto" />
                                <p className="mt-1.5 text-[11.5px] font-black text-slate-900 leading-none">{CREATOR.name}</p>
                                <p className="mt-1 text-[8px] font-bold text-primary-700 leading-none">
                                    astreona.com/{CREATOR.handle}
                                </p>
                            </div>

                            <div className="px-2 pb-2">
                                <p className="text-[8px] font-black uppercase tracking-[0.16em] text-slate-400 mb-1.5 px-1">
                                    Your links
                                </p>
                                <div className="space-y-1.5">
                                    {slots.map((slot, index) => (
                                        <LinkSlot key={index} slot={slot}
                                            slotRef={(node) => { slotRefs.current[index] = node; }} />
                                    ))}
                                </div>
                                {/* Coffee brown rather than the brand teal, and the
                                    real `coffee256.png` the app uses — a coffee
                                    button in the product's own colour reads as one
                                    more platform row instead of a tip jar. */}
                                <div className="mt-2 rounded-xl text-white text-[9px] font-black py-2
                                        flex items-center justify-center gap-1.5 shadow-[0_8px_18px_-10px_rgba(92,48,26,0.95)]"
                                    style={{ background: 'linear-gradient(120deg,#a75c31 0%,#7a3f1e 52%,#4d2812 100%)' }}>
                                    <img src="/coffee256.png" alt="" aria-hidden="true" className="w-3.5 h-3.5 object-contain" />
                                    Buy me a coffee · $5
                                </div>
                                <span className="block mx-auto mt-2.5 mb-1 w-[62px] h-[3px] rounded-full bg-slate-300" aria-hidden="true" />
                            </div>
                        </PhoneShell>
                        {/* <p className="mt-2.5 text-center text-[10px] text-slate-400">Example profile</p> */}
                    </div>
                </div>

                {flights.map((flight) => (
                    <LinkFlight key={flight.id} item={flight.item}
                        from={flight.from} via={flight.via} to={flight.to} />
                ))}
            </div>
        </section>
    );
}

/* ────────────────────────  what the others keep  ─────────────────────── */

/** Column proportions, in percent, declared once: the highlighted row paints a
 *  single gradient across five separate cells and has to know where each of
 *  them sits inside it (see `usSlice`). `table-fixed` is what holds them. */
const COMPARE_COLS = [
    { key: 'name', label: 'Platform', width: 38, align: 'text-left' },
    { key: 'monthly', label: 'Monthly', width: 12, align: 'text-right' },
    { key: 'cut', label: 'Their cut', width: 13, align: 'text-right' },
    { key: 'keeps', label: 'You keep', width: 21, align: 'text-right' },
    { key: 'lost', label: 'You lose', width: 16, align: 'text-right' },
];

/** A zero renders as an em-dash. A column of "$0.00"s reads as a charge that
 *  happened to come to nothing this month; a dash reads as no charge at all. */
const dashIfZero = (value, decimals = 2) => (value === 0 ? '—' : usd(value, decimals));

/** One gradient, five cells. Set on the `<tr>` it paints behind the cells and
 *  their rounded corners cannot clip it; set on each cell it restarts five
 *  times and bands at every seam. So every cell paints its own slice of one
 *  row-wide gradient — exact, because the column widths above are fixed and
 *  the table's border spacing is zero. Same two stops as the hero's fee badge
 *  (`from-primary-600 to-primary-700`): one claim, made twice. */
const usSlice = (index) => {
    const width = COMPARE_COLS[index].width;
    const before = COMPARE_COLS.slice(0, index).reduce((sum, col) => sum + col.width, 0);
    return {
        backgroundImage: 'linear-gradient(90deg,#20c9b5 0%,#1aab99 100%)',
        backgroundSize: `${((100 / width) * 100).toFixed(3)}% 100%`,
        backgroundPosition: `${((before / (100 - width)) * 100).toFixed(3)}% 0`,
    };
};

/**
 * The mark beside a name in the table.
 *
 * Two sources, because the marks come in two forms.
 *
 * Ko-fi, Buy Me a Coffee, Patreon and Linktree are vector, and resolve through
 * `detectPlatform` off the `site` on their row — the same lookup a pasted link
 * uses, so none of them is drawn by hand and adding another is a row in
 * `utils/platformIcons`, not a path typed into this file.
 *
 * Beacons, Stan Store and Collabstr are in no public icon set, so they are the
 * app icons themselves, in `public/compare/`. Held as files rather than traced
 * into `PLATFORMS` for the same reason nothing here is drawn from memory: a
 * mark redrawn by eye is a wrong mark, and on a table whose whole claim is
 * that its figures are checkable, a wrong one is the expensive kind of
 * mistake. They are served from our own origin rather than hot-linked, so the
 * table does not break the day somebody else's CDN changes a URL.
 *
 * The white disc under them is not decoration either: two of the three fill
 * their frame, but Collabstr's is a mark on transparency, and without a disc
 * it reads as two pink shapes floating on the row rather than as a logo.
 *
 * Decorative, and marked so: the name it sits next to is the row's heading,
 * and a screen reader reading "Ko-fi, Ko-fi" is worse than one reading it once.
 */
function CompareMark({ row }) {
    const image = row.us ? '/logo512.png' : row.logo;

    return (
        <span aria-hidden="true" className="flex-shrink-0">
            {image
                ? <img src={image} alt="" width={26} height={26} loading="lazy" decoding="async"
                    className={`w-[26px] h-[26px] rounded-full object-cover ${row.us
                        ? 'ring-2 ring-white/70' : 'bg-white ring-1 ring-slate-200'}`} />
                : <PlatformIcon url={row.site} size={26} />}
        </span>
    );
}

function CompareRow({ row }) {
    const us = Boolean(row.us);
    const cell = `px-2.5 sm:px-4 align-middle ${us ? 'py-4' : 'py-3.5 border-b border-slate-200'}`;
    const slice = (index) => (us ? usSlice(index) : undefined);

    return (
        <tr className={us ? 'shadow-[0_18px_44px_-20px_rgba(32,201,181,0.95)]' : ''}>
            <th scope="row" style={slice(0)} className={`${cell} text-left font-normal ${us ? 'rounded-l-2xl' : ''}`}>
                <span className="flex items-start gap-2.5">
                    <CompareMark row={row} />
                    <span className="block min-w-0">
                        <span className={`block text-[15px] font-black ${us ? 'text-white' : 'text-slate-900'}`}>{row.name}</span>
                        <span className={`block text-[11px] ${us ? 'text-white/75' : 'text-slate-500'}`}>{row.plan}</span>
                        <span className={`block text-[11px] leading-snug mt-1 ${us ? 'text-white/65' : 'text-slate-400'}`}>{row.note}</span>
                    </span>
                </span>
            </th>
            <td style={slice(1)} className={`${cell} text-right text-[13px] font-bold tabular-nums ${us ? 'text-white/85' : 'text-slate-600'}`}>
                {dashIfZero(row.monthly, 0)}
            </td>
            <td style={slice(2)} className={`${cell} text-right text-[13px] font-bold tabular-nums ${us ? 'text-white' : 'text-slate-600'}`}>
                {row.cut}%
            </td>
            <td style={slice(3)} className={`${cell} text-right tabular-nums ${us ? 'text-[22px] font-black text-white' : 'text-[15px] font-black text-slate-900'}`}>
                {usd(row.keeps)}
            </td>
            <td style={slice(4)} className={`${cell} text-right text-[13px] font-bold tabular-nums ${us ? 'text-white/75 rounded-r-2xl' : 'text-slate-400'}`}>
                {dashIfZero(row.lost)}
            </td>
        </tr>
    );
}

/** The same eight rows below `sm`. Five columns do not survive 375px, and a
 *  table you have to drag sideways is one nobody reads to the end of. */
function CompareCard({ row }) {
    const us = Boolean(row.us);
    return (
        <div className={`rounded-2xl p-4 ${us
            ? 'bg-gradient-to-r from-primary-600 to-primary-700 shadow-[0_18px_44px_-20px_rgba(32,201,181,0.95)]'
            : 'bg-white border border-slate-200'}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1 flex items-start gap-2.5">
                    <CompareMark row={row} />
                    <div className="min-w-0">
                        <p className={`m-0 text-[15px] font-black ${us ? 'text-white' : 'text-slate-900'}`}>{row.name}</p>
                        <p className={`m-0 text-[12px] ${us ? 'text-white/75' : 'text-slate-500'}`}>{row.plan}</p>
                    </div>
                </div>
                <div className="text-right flex-shrink-0">
                    <p className={`m-0 font-black tabular-nums leading-none ${us ? 'text-[22px] text-white' : 'text-[19px] text-slate-900'}`}>
                        {usd(row.keeps)}
                    </p>
                    <p className={`m-0 mt-1 text-[12px] ${us ? 'text-white/75' : 'text-slate-400'}`}>you keep</p>
                </div>
            </div>
            <p className={`m-0 mt-2.5 text-[12px] font-semibold tabular-nums ${us ? 'text-white/85' : 'text-slate-600'}`}>
                {row.cut}% cut · {row.monthly === 0 ? 'nothing monthly' : `${usd(row.monthly, 0)}/mo`}
                {row.lost > 0 ? ` · ${usd(row.lost)} lost` : ''}
            </p>
            <p className={`m-0 mt-1.5 text-[12px] leading-snug ${us ? 'text-white/65' : 'text-slate-400'}`}>{row.note}</p>
        </div>
    );
}

/**
 * The visitor already has Linktree, or Ko-fi, or Patreon. They do not need to
 * be told what a tip is — they need one number that makes switching obviously
 * correct, which is why this sits third, before the page explains anything
 * about itself. Everything after it is the substantiation.
 *
 * Our own figures come from `FEE_PERCENT` like every other fee on the page;
 * the competitors' come from their public pricing pages on the date printed
 * under the table. Their notes are printed with them because leaving them off
 * would make the comparison unfair — Ko-fi really does take nothing on a tip —
 * and an unfair table is worth less than no table.
 */
function ComparisonSection({ onSignup }) {
    const us = COMPARISON[0];
    const collabstr = comparisonRow('Collabstr');
    const bothSides = (COLLABSTR_EXAMPLE * (collabstr.cut + COLLABSTR_BRAND_CUT)) / 100;

    const proof = [
        {
            k: `${usd(us.monthly, 0)}/month`,
            v: `No plan to buy. No tier that unlocks ${FEE_PERCENT}%. The free account is the whole product.`,
        },
        {
            k: 'Nothing per payout',
            v: 'The fee column on every ledger row reads zero. Gross and net are the same figure.',
        },
        {
            k: 'Both sides, zero',
            v: `Collabstr charges the brand ${COLLABSTR_BRAND_CUT}% and takes ${collabstr.cut}% from you. On a ${usd(COLLABSTR_EXAMPLE, 0)} campaign that's ${usd(bothSides, 0)} that reaches nobody who made anything.`,
            hero: true,
        },
    ];

    return (
        <section id="compare" className="bg-slate-50 py-16 sm:py-24 border-y border-slate-100">
            <div className="max-w-5xl mx-auto px-4 sm:px-6">
                <Reveal>
                    <SectionHead
                        eyebrow="The honest version"
                        title={`What each platform keeps from the same ${usd(COMPARISON_BASE, 0)}`}
                        desc="Same month, same income, eight platforms. The only number that changes is what reaches you."
                    />
                </Reveal>

                <Reveal delay={60}>
                    <div className="hidden sm:block">
                        <table className="w-full table-fixed border-separate border-spacing-0 text-left">
                            <caption className="sr-only">
                                What each platform keeps from the same {usd(COMPARISON_BASE, 0)} month
                            </caption>
                            <colgroup>
                                {COMPARE_COLS.map((col) => <col key={col.key} style={{ width: `${col.width}%` }} />)}
                            </colgroup>
                            <thead>
                                <tr>
                                    {COMPARE_COLS.map((col) => (
                                        <th key={col.key} scope="col"
                                            className={`pb-3 px-2.5 sm:px-4 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 ${col.align}`}>
                                            {col.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                <CompareRow row={us} />
                                <tr aria-hidden="true">
                                    <td colSpan={COMPARE_COLS.length} className="pt-4 pb-3">
                                        <span className="block h-px bg-slate-200" />
                                    </td>
                                </tr>
                                {COMPARISON.slice(1).map((row) => <CompareRow key={row.name} row={row} />)}
                            </tbody>
                        </table>
                    </div>
                </Reveal>

                <div className="sm:hidden space-y-2.5">
                    {COMPARISON.map((row, index) => (
                        <Reveal key={row.name} delay={index * 70}>
                            <CompareCard row={row} />
                        </Reveal>
                    ))}
                </div>

                <div className="grid sm:grid-cols-3 gap-4 mt-8">
                    {proof.map((item, index) => (
                        <Reveal key={item.k} delay={index * 70}>
                            <div className={`h-full rounded-2xl border p-5 ${item.hero
                                ? 'border-primary-200 bg-primary-50/40'
                                : 'border-slate-200 bg-white'}`}>
                                <p className="text-lg font-black text-slate-900 tracking-tight tabular-nums m-0">{item.k}</p>
                                <p className="mt-1.5 text-[13px] text-slate-600 leading-relaxed m-0">{item.v}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>

                <Reveal delay={80}>
                    <p className="mt-6 text-[11px] text-slate-400 leading-relaxed">
                        Competitor pricing verified {COMPARISON_CHECKED} from each platform's public
                        pricing page. Plans change; we re-check quarterly.
                    </p>

                    <div className="mt-6 text-center">
                        <button type="button" onClick={onSignup}
                            className="group inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full text-[15px] font-black text-white
                                bg-gradient-to-r from-primary-400 to-primary-600
                                shadow-[0_18px_50px_-16px_rgba(45,217,195,0.95)] hover:-translate-y-0.5 active:translate-y-0 transition">
                            Start keeping {100 - FEE_PERCENT}%
                            <Icon path={PATHS.arrow} className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                        </button>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

/* ──────────────────────────  ways to earn  ───────────────────────────── */

const STREAMS = [
    {
        key: 'campaign',
        title: 'Brand campaigns',
        line: 'Apply, get approved, get funded.',
        body: 'Brands post a budget; approval locks it in escrow before you shoot a frame.',
        // The one figure in this list that is a claim about the marketplace
        // rather than about the example account, so it is read off the
        // marketplace: `MarketplaceSection` prints the same number a screen
        // later, and a visitor can count the cards. It used to say
        // '$6,200 · top open campaign', which was an invented campaign from
        // `SHOWCASE` and is now contradicted by the row below it.
        stat: String(MARKET_TOTALS.campaigns), statLabel: 'campaigns open now',
        icon: PATHS.briefcase,
    },
    {
        key: 'subscription',
        title: 'Subscriptions',
        line: 'Recurring income that shows up every month.',
        body: 'Set a monthly price for subscriber-only messages and content — renewals are ours.',
        stat: '147', statLabel: 'active subscribers',
        icon: PATHS.users,
    },
    {
        key: 'coffee',
        title: 'Coffees',
        line: 'The one-tap thank-you.',
        body: 'A coffee and a note, in seconds — no account, no subscription needed.',
        stat: '200', statLabel: 'coffees this month',
        icon: null, image: '/coffee256.png',
    },
    {
        key: 'star',
        title: 'Star gifts',
        line: 'Live appreciation, inside the chat.',
        body: 'Fans send stars mid-conversation, one star to the dollar, straight to your balance.',
        stat: '860', statLabel: 'stars received',
        icon: null, image: '/star192.png',
    },
];

function EarnSection() {
    return (
        <section id="earn" className="bg-white py-14 sm:py-20">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
                <Reveal>
                    <SectionHead
                        eyebrow="Ways to earn"
                        title="Four income streams, one balance"
                    />
                </Reveal>

                <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
                    {STREAMS.map((stream, index) => {
                        const tone = SERIES_COLORS[stream.key];
                        return (
                            <Reveal key={stream.key} delay={index * 70}>
                                <div className="group h-full rounded-3xl border border-slate-200 bg-white p-6 sm:p-7 transition
                                    hover:border-transparent hover:shadow-[0_24px_60px_-30px_rgba(15,23,42,0.35)] hover:-translate-y-1"
                                    style={{ borderTopColor: tone, borderTopWidth: 3 }}>
                                    <div className="flex items-start gap-4">
                                        <span className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                                            style={{ background: `${tone}18`, color: tone }}>
                                            {stream.image
                                                ? <img src={stream.image} alt="" className="w-6 h-6 object-contain" />
                                                : <Icon path={stream.icon} className="w-6 h-6" />}
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <h3 className="text-lg font-black text-slate-900">{stream.title}</h3>
                                            <p className="text-sm font-semibold" style={{ color: tone }}>{stream.line}</p>
                                        </div>
                                    </div>

                                    <p className="mt-4 text-[14px] text-slate-600 leading-relaxed">{stream.body}</p>

                                    <div className="mt-5 pt-4 border-t border-slate-100 flex items-baseline gap-2">
                                        <span className="text-2xl font-black tabular-nums" style={{ color: tone }}>{stream.stat}</span>
                                        <span className="text-xs text-slate-400 font-semibold">{stream.statLabel}</span>
                                    </div>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

/* ──────────────────────  who is actually on it  ──────────────────────── */

/**
 * A row that scrolls forever.
 *
 * The track holds the same group twice and slides exactly half its own width,
 * which is one group — so the copy arrives where the original started and the
 * loop has no seam. Both groups therefore have to be identical down to their
 * padding; that is why the gap after the last card lives on the group rather
 * than between the two of them.
 *
 * The duplicate is `aria-hidden` — a screen reader should hear this list once
 * — and also `inert`, set on the node because React 18 does not pass the
 * attribute through. `aria-hidden` alone would leave a dozen links hidden from
 * a screen reader but still in the tab order, which is worse than either. On a
 * browser without `inert` the assignment is a no-op and the copy stays
 * tabbable, which is exactly where it would have been anyway.
 *
 * Pointing at the row or tabbing into it drops it to a crawl rather than
 * stopping it: a card that is going past at reading speed is hard to click,
 * but a row that halts under the cursor reads as broken. The rate change goes
 * through the animation object instead of CSS because `updatePlaybackRate`
 * keeps the current position and eases into the new speed — re-declaring
 * `animation-duration` mid-run recomputes progress from elapsed time and the
 * row jumps. A browser without `getAnimations` simply keeps full speed.
 *
 * Under `prefers-reduced-motion` there is no animation to slow down, and the
 * whole thing degrades to a plain horizontal scroller with the duplicate
 * dropped.
 */
const MARQUEE_HOVER_RATE = 0.22;

function Marquee({ label, seconds, reverse = false, children }) {
    const track = useRef(null);
    const copy = useRef(null);

    useEffect(() => {
        if (copy.current) copy.current.inert = true;
    }, []);

    const rate = (value) => {
        const node = track.current;
        if (!node || typeof node.getAnimations !== 'function') return;
        node.getAnimations().forEach((animation) => {
            if (typeof animation.updatePlaybackRate === 'function') animation.updatePlaybackRate(value);
            else animation.playbackRate = value;
        });
    };

    return (
        <div className="astreona-marquee" role="group" aria-label={label}
            onMouseEnter={() => rate(MARQUEE_HOVER_RATE)} onMouseLeave={() => rate(1)}
            onFocus={() => rate(MARQUEE_HOVER_RATE)} onBlur={() => rate(1)}>
            <div ref={track} className="astreona-marquee-track"
                style={{ animationDuration: `${seconds}s`, animationDirection: reverse ? 'reverse' : 'normal' }}>
                <div className="astreona-marquee-group">{children}</div>
                <div className="astreona-marquee-group" ref={copy} aria-hidden="true">{children}</div>
            </div>
        </div>
    );
}

/** A real profile picture, with the page's own initials tile behind it.
 *
 *  These come off the API host rather than out of `public/`, on purpose: a
 *  creator who changes their photo changes it here, and one who deletes their
 *  account takes it with them. A copy checked into the build would do neither.
 *  `loading="lazy"` is what keeps that affordable — the rows sit several
 *  screens down, so nothing here is fetched by a visitor who never reaches
 *  them, and `onError` covers the host being unreachable when they do. */
function MarketAvatar({ src, name, size = 44 }) {
    const [failed, setFailed] = useState(false);

    if (failed || !src) {
        return <Avatar name={name} size={size} gradient="linear-gradient(135deg,#20c9b5,#0f6f63)" />;
    }

    return (
        <img src={src} alt="" aria-hidden="true" loading="lazy" decoding="async"
            width={size} height={size} onError={() => setFailed(true)}
            className="rounded-full object-cover flex-shrink-0 bg-slate-100"
            style={{ width: size, height: size }} />
    );
}

function MarketBrandChip({ brand }) {
    return (
        <a href={`/${brand.u}`} target="_blank" rel="noopener noreferrer"
            className="group flex-shrink-0 flex items-center gap-3 pl-3 pr-3.5 py-2.5 rounded-2xl bg-white border border-slate-200
                shadow-[0_10px_30px_-24px_rgba(15,23,42,0.6)] transition
                hover:border-primary-300 hover:shadow-[0_18px_40px_-26px_rgba(15,23,42,0.45)]">
            <MarketAvatar src={brand.pp} name={brand.name} size={40} />
            <span className="block">
                <span className="block text-[13.5px] font-black text-slate-900 whitespace-nowrap leading-tight">{brand.name}</span>
                <span className="block text-[11px] text-slate-400 whitespace-nowrap leading-tight mt-0.5">
                    {compact(brand.followers)} followers · {brand.tag}
                </span>
            </span>
            <span className="ml-1 px-2 py-0.5 rounded-full bg-primary-50 border border-primary-200
                text-[10px] font-black text-primary-700 whitespace-nowrap">
                {brand.campaigns} open
            </span>
        </a>
    );
}

function MarketCampaignCard({ campaign }) {
    return (
        <a href={`/${campaign.u}`} target="_blank" rel="noopener noreferrer"
            className="w-[286px] flex-shrink-0 flex flex-col rounded-2xl bg-white border border-slate-200 p-4
                shadow-[0_10px_30px_-24px_rgba(15,23,42,0.6)] transition
                hover:border-primary-300 hover:shadow-[0_20px_44px_-26px_rgba(15,23,42,0.45)]">
            <div className="flex items-center gap-2.5">
                <MarketAvatar src={campaign.pp} name={campaign.brand} size={28} />
                <span className="flex-1 min-w-0 text-[12px] font-bold text-slate-500 truncate">{campaign.brand}</span>
                <span className="text-[10px] font-black text-slate-400 whitespace-nowrap">{campaign.slots} slots</span>
            </div>

            <p className="mt-3 text-[14px] font-black text-slate-900 leading-snug line-clamp-2">{campaign.title}</p>

            <div className="mt-auto pt-3.5 flex items-center gap-2">
                <span className="flex-shrink-0 px-2.5 py-1 rounded-lg bg-primary-50 border border-primary-200
                    text-[11px] font-black text-primary-700 tabular-nums whitespace-nowrap">
                    {payLabel(campaign)}
                </span>
                {payExtras(campaign) && (
                    <span className="min-w-0 truncate text-[10px] font-bold text-slate-400">
                        + {payExtras(campaign)}
                    </span>
                )}
                <span className="ml-auto flex flex-shrink-0 items-center gap-1">
                    {campaign.platforms.slice(0, 3).map((platform) => (
                        <PlatformIcon key={platform} platform={platform} size={17} />
                    ))}
                </span>
            </div>
        </a>
    );
}

function MarketCreatorCard({ creator }) {
    return (
        <a href={`/${creator.u}`} target="_blank" rel="noopener noreferrer"
            className="w-[224px] flex-shrink-0 flex flex-col items-center text-center rounded-2xl bg-white border border-slate-200 px-4 py-5
                shadow-[0_10px_30px_-24px_rgba(15,23,42,0.6)] transition
                hover:border-primary-300 hover:shadow-[0_20px_44px_-26px_rgba(15,23,42,0.45)]">
            <span className="relative">
                <MarketAvatar src={creator.pp} name={creator.u} size={60} />
                {creator.verified && (
                    <span title="Verified"
                        className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-primary-600 text-white
                            border-2 border-white flex items-center justify-center">
                        <Icon path={PATHS.check} className="w-2.5 h-2.5" strokeWidth={4} />
                    </span>
                )}
            </span>

            <p className="mt-3 max-w-full truncate text-[13.5px] font-black text-slate-900">@{creator.u}</p>
            <p className="text-[11px] font-semibold text-slate-400 tabular-nums">{compact(creator.followers)} followers</p>

            <span className="mt-3 flex items-center justify-center gap-2">
                {creator.platforms.slice(0, 3).map(({ p, f }) => (
                    <span key={p} className="flex items-center gap-1">
                        <PlatformIcon platform={p} size={15} />
                        <span className="text-[10px] font-bold text-slate-500 tabular-nums">{compact(f)}</span>
                    </span>
                ))}
            </span>

            {creator.tags.length > 0 && (
                <span className="mt-3 flex flex-wrap justify-center gap-1">
                    {creator.tags.map((tag) => (
                        <span key={tag}
                            className="px-2 py-0.5 rounded-full bg-slate-100 text-[10px] font-bold text-slate-500 whitespace-nowrap">
                            {tag}
                        </span>
                    ))}
                </span>
            )}
        </a>
    );
}

function MarketStat({ value, label }) {
    return (
        <div className="text-center px-2">
            <p className="text-[26px] sm:text-[32px] font-black text-slate-900 tabular-nums leading-none">{value}</p>
            <p className="mt-2 text-[10px] sm:text-[11px] font-black uppercase tracking-[0.16em] text-slate-400">{label}</p>
        </div>
    );
}

/** The rows run edge to edge while the copy stays in the column the rest of
 *  the page uses, so the label sits over the row rather than beside it. */
function MarketRow({ label, children }) {
    return (
        <div>
            <div className="max-w-6xl mx-auto px-4 sm:px-6 mb-1">
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{label}</span>
            </div>
            {children}
        </div>
    );
}

/**
 * The one section on this page that is not about Astreona: it is about who is
 * already standing in it.
 *
 * It sits directly after the four income streams because the first of them —
 * brand campaigns — is the only one a visitor has no way of checking for
 * themselves. Three rows answer the three halves of that: the brands, what
 * they are actually paying for, and the creators already in the room. Every
 * card opens the account it belongs to, in a new tab so that checking one
 * does not cost us the visitor. A campaign card opens the brand behind the
 * brief rather than the brief itself, because campaign detail is behind the
 * login and a card that lands a signed-out visitor on a wall is worse than
 * one that lands them on the brand.
 *
 * The four figures on top count the **whole** catalogue, not the sample the
 * rows have room for, and they are the plainest true numbers available: how
 * many accounts, how many briefs, how many places on those briefs, and what
 * the briefs add up to. A marketplace this size is better served by a real
 * $13,300 than by a rounded-up number nobody can check.
 */
function MarketplaceSection() {
    return (
        <section id="marketplace" className="relative overflow-hidden astreona-market py-14 sm:py-20 border-t border-slate-100">
            <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
                {/* <Reveal>
                    <SectionHead
                        eyebrow="Live on Astreona"
                        title="There is already work on the board"
                        desc="Every card below belongs to an account you can open right now — brands with campaigns on the board, the briefs they are paying for, and the creators who can apply to them. The figures are the ones each account publishes."
                    />
                </Reveal> */}

                <Reveal delay={60}>
                    <div className="mb-10 sm:mb-12 grid grid-cols-2 sm:grid-cols-3 gap-y-7 rounded-3xl
                        border border-slate-200 bg-white/70 backdrop-blur px-4 py-7 sm:divide-x sm:divide-slate-200">
                        <MarketStat value={MARKET_TOTALS.brands} label="Brand accounts" />
                        <MarketStat value={MARKET_TOTALS.campaigns} label="Open campaigns" />
                        <MarketStat value={MARKET_TOTALS.slots} label="Creator slots" />
                        {/* <MarketStat value={usd(MARKET_TOTALS.budget, 0)} label="In listed budgets" /> */}
                    </div>
                </Reveal>
            </div>

            <Reveal delay={100} className="relative space-y-5 sm:space-y-6">
                <MarketRow label="The brands">
                    <Marquee label="Brand accounts on Astreona" seconds={130}>
                        {MARKET_BRANDS.map((brand) => <MarketBrandChip key={brand.u} brand={brand} />)}
                    </Marquee>
                </MarketRow>

                <MarketRow label="What they are paying for">
                    <Marquee label="Open campaigns" seconds={96} reverse>
                        {MARKET_CAMPAIGNS.map((campaign) => (
                            <MarketCampaignCard key={`${campaign.u}-${campaign.title}`} campaign={campaign} />
                        ))}
                    </Marquee>
                </MarketRow>

                <MarketRow label="Who is already here">
                    <Marquee label="Creators on Astreona" seconds={58}>
                        {MARKET_CREATORS.map((creator) => <MarketCreatorCard key={creator.u} creator={creator} />)}
                    </Marquee>
                </MarketRow>
            </Reveal>

            {/* <div className="relative max-w-3xl mx-auto px-4 sm:px-6 mt-9">
                <p className="text-center text-[11.5px] text-slate-400 leading-relaxed">
                    Read from the public profile API in {MARKET_CHECKED}. Follower counts are the figures each
                    account publishes on its own profile, not ours — every card opens the account it belongs to.
                </p>
            </div> */}
        </section>
    );
}

/* ────────────────────────  the finance dashboard  ────────────────────── */

function BreakdownMock() {
    const data = BREAKDOWN.map((row) => ({ key: row.source, label: row.label, value: row.net }));

    return (
        <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
            <div className="flex items-center justify-between gap-3">
                <h3 className="text-base font-bold text-slate-800">Income breakdown</h3>
                <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">Last 30 days</span>
            </div>
            <p className="text-xs text-slate-400 mb-4">Gross equals net on every row — the fee column is there, and it is zero.</p>

            <div className="flex flex-col sm:flex-row items-center gap-5">
                <DonutChart data={data} centerLabel="Net" format={usdShort} size={180} />

                <div className="flex-1 w-full space-y-2">
                    {BREAKDOWN.map((row) => (
                        <div key={row.source} className="flex items-center gap-2.5">
                            <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: SERIES_COLORS[row.source] }} />
                            <span className="min-w-0 flex-1">
                                <span className="block text-xs font-semibold text-slate-700 truncate">{row.label}</span>
                                <span className="block text-[10px] text-slate-400 truncate">{row.meta}</span>
                            </span>
                            <span className="text-xs font-bold text-slate-800 tabular-nums flex-shrink-0">{usd(row.net)}</span>
                            <span className="text-[10px] text-slate-400 w-9 text-right tabular-nums flex-shrink-0">
                                {Math.round((row.net / TOTALS.net) * 100)}%
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="mt-5 overflow-x-auto">
                <table className="w-full text-xs min-w-[320px]">
                    <thead>
                        <tr className="text-slate-400 border-b border-slate-100">
                            <th className="text-left font-semibold py-2">Source</th>
                            <th className="text-right font-semibold py-2">Gross</th>
                            <th className="text-right font-semibold py-2">Fee</th>
                            <th className="text-right font-semibold py-2">Net</th>
                        </tr>
                    </thead>
                    <tbody>
                        {BREAKDOWN.map((row) => (
                            <tr key={row.source} className="border-b border-slate-50">
                                <td className="py-2 text-slate-700 font-medium">
                                    <span className="flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: SERIES_COLORS[row.source] }} />
                                        {row.label}
                                    </span>
                                </td>
                                <td className="py-2 text-right text-slate-600 tabular-nums">{usd(row.gross)}</td>
                                <td className="py-2 text-right text-slate-400 tabular-nums">−{usd(row.fee)}</td>
                                <td className="py-2 text-right text-slate-900 font-bold tabular-nums">{usd(row.net)}</td>
                            </tr>
                        ))}
                        <tr className="font-bold">
                            <td className="py-2 text-slate-800">Total</td>
                            <td className="py-2 text-right text-slate-700 tabular-nums">{usd(TOTALS.gross)}</td>
                            <td className="py-2 text-right text-slate-400 tabular-nums">−{usd(TOTALS.fee)}</td>
                            <td className="py-2 text-right text-slate-900 tabular-nums">{usd(TOTALS.net)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </section>
    );
}

/* ─────────────────────────────  campaigns  ───────────────────────────── */

function CampaignCard({ campaign }) {
    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 hover:border-primary-300 hover:shadow-[0_18px_44px_-28px_rgba(15,23,42,0.45)] transition">
            <div className="flex items-start gap-3">
                <Avatar name={campaign.brand} size={40} gradient="linear-gradient(135deg,#334155,#0f172a)" />
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <p className="text-sm font-black text-slate-900 truncate">{campaign.title}</p>
                        {campaign.hot && (
                            <span className="px-1.5 py-0.5 rounded bg-orange-50 border border-orange-200 text-[9px] font-black text-orange-600 uppercase tracking-wide flex-shrink-0">
                                Hot
                            </span>
                        )}
                    </div>
                    <p className="text-[11px] text-slate-500 truncate">
                        {campaign.brand} · <span className="text-amber-600 font-bold">{campaign.rating}★</span> over {campaign.deals} paid deals
                    </p>
                </div>
                <div className="text-right flex-shrink-0">
                    <p className="text-lg font-black text-primary-700 tabular-nums leading-none">{usd(campaign.budget, 0)}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">budget</p>
                </div>
            </div>

            <p className="mt-3 text-xs text-slate-600">{campaign.formats}</p>

            <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                {campaign.platforms.map((platform) => (
                    <span key={platform} className="px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-[10px] font-bold text-slate-600">
                        {platform}
                    </span>
                ))}
                <span className="ml-auto text-[10px] text-slate-400 font-semibold">{campaign.applicants} applicants</span>
            </div>
        </div>
    );
}

const TIMELINE = [
    { title: 'Application approved', body: 'Lumière Beauty accepted your $4,500 proposal.', tone: 'slate' },
    { title: 'Funded in escrow', body: '$4,500 debited from the brand wallet and locked. It cannot be pulled back.', tone: 'primary' },
    { title: 'Draft delivered', body: 'Submitted 2 days before the deadline, from a saved template.', tone: 'slate' },
    { title: 'Brand approved the delivery', body: 'No revision requested — unreviewed deliveries release to you automatically anyway.', tone: 'slate' },
    { title: 'Escrow released', body: `${usd(netOf(4500), 0)} booked to your withdrawable balance — the full amount, with no commission taken.`, tone: 'success' },
];

function DealTimeline() {
    const dot = {
        slate: 'bg-slate-300',
        primary: 'bg-primary-500 ring-4 ring-primary-100',
        success: 'bg-emerald-500 ring-4 ring-emerald-100',
    };
    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between gap-3 pb-4 border-b border-slate-100">
                <div className="min-w-0">
                    <p className="text-sm font-black text-slate-900 truncate">Summer Fragrance Launch</p>
                    <p className="text-[11px] text-slate-500">Lumière Beauty · deal #4812</p>
                </div>
                <span className="px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] font-black text-emerald-700 flex-shrink-0">
                    COMPLETED
                </span>
            </div>

            <ol className="list-none p-0 m-0 mt-4 space-y-4">
                {TIMELINE.map((step, index) => (
                    <li key={step.title} className="relative pl-7">
                        {index < TIMELINE.length - 1 && (
                            <span className="absolute left-[5px] top-4 bottom-[-16px] w-px bg-slate-200" aria-hidden="true" />
                        )}
                        <span className={`absolute left-0 top-1.5 w-2.5 h-2.5 rounded-full ${dot[step.tone]}`} aria-hidden="true" />
                        <p className="text-[13px] font-bold text-slate-800 leading-snug">{step.title}</p>
                        <p className="text-[11.5px] text-slate-500 leading-relaxed mt-0.5">{step.body}</p>
                    </li>
                ))}
            </ol>

            <div className="mt-4 rounded-xl bg-gradient-to-r from-emerald-50 to-white border border-emerald-200 p-3.5 flex items-center gap-3">
                <span className="w-9 h-9 rounded-xl bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
                    <Icon path={PATHS.check} className="w-5 h-5" strokeWidth={3} />
                </span>
                <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-bold text-emerald-700 uppercase tracking-wide">Payment released</p>
                    <p className="text-lg font-black text-slate-900 tabular-nums leading-tight">{usd(netOf(4500))}</p>
                </div>
                <span className="text-[10px] text-slate-400 text-right leading-tight flex-shrink-0">
                    gross {usd(4500, 0)}<br />fee −{usd(feeOf(4500), 0)}
                </span>
            </div>
        </div>
    );
}

/* ───────────────────────────  the profile  ───────────────────────────── */

function ProfileMock() {
    return (
        <Frame url={`astreona.com/${CREATOR.handle}`}>
            <div className="bg-white">
                <div className="h-24 bg-gradient-to-r from-primary-500 via-primary-600 to-primary-800" />
                <div className="px-4 sm:px-6 pb-6 -mt-11">
                    <div className="flex items-end gap-3">
                        <Avatar name={CREATOR.name} size={84} gradient={CREATOR.gradient}
                            className="ring-4 ring-white shadow-lg" />
                        <div className="min-w-0 flex-1 translate-y-2">
                            <div className="flex items-center gap-1.5">
                                <h3 className="text-lg font-black text-slate-900 truncate">{CREATOR.name}</h3>
                                {/* Same verified-creator mark the real profile uses
                                    (`pages/User.jsx`) — a mock wearing a badge the
                                    product does not have is the kind of small lie
                                    that costs the whole page its credibility. */}
                                <span className="w-[18px] h-[18px] rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" className="w-3 h-3 text-white" fill="currentColor" aria-hidden="true">
                                        <path d="M10.067.87a2.89 2.89 0 0 0-4.134 0l-.622.638-.89-.011a2.89 2.89 0 0 0-2.924 2.924l.01.89-.636.622a2.89 2.89 0 0 0 0 4.134l.637.622-.011.89a2.89 2.89 0 0 0 2.924 2.924l.89-.01.622.636a2.89 2.89 0 0 0 4.134 0l.622-.637.89.011a2.89 2.89 0 0 0 2.924-2.924l-.01-.89.636-.622a2.89 2.89 0 0 0 0-4.134l-.637-.622.011-.89a2.89 2.89 0 0 0-2.924-2.924l-.89.01-.622-.636zM6.854 11.354l-2.5-2.5.708-.708L6.854 9.9l3.792-3.793.708.708-4.5 4.5z" />
                                    </svg>
                                </span>
                            </div>
                            <p className="text-[12px] text-slate-500">@{CREATOR.handle}</p>
                        </div>
                    </div>

                    <p className="mt-3 text-[13px] text-slate-600 leading-relaxed">{CREATOR.bio}</p>

                    <div className="mt-3 flex items-center gap-2 rounded-xl bg-slate-50 border border-slate-200 px-3 py-2">
                        <Icon path={PATHS.link} className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="text-[12px] font-semibold text-slate-700 truncate flex-1">
                            astreona.com/{CREATOR.handle}
                        </span>
                        <span className="text-[10px] font-black text-primary-700 bg-primary-50 border border-primary-200 px-2 py-0.5 rounded flex-shrink-0">
                            COPY
                        </span>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-bold text-emerald-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Open to work · from 1 Sep
                        </span>
                        {['Beauty', 'Lifestyle', 'Travel'].map((tag) => (
                            <span key={tag} className="px-2.5 py-1 rounded-full bg-slate-100 border border-slate-200 text-[11px] font-semibold text-slate-600">
                                {tag}
                            </span>
                        ))}
                    </div>

                    <div className="grid grid-cols-3 gap-2 mt-4">
                        {[['184K', 'Followers'], ['61.2K', 'Avg. reach'], ['%12.4', 'Engagement']].map(([value, label]) => (
                            <div key={label} className="rounded-xl bg-slate-50 border border-slate-100 p-2.5 text-center">
                                <p className="text-base font-black text-slate-800 tabular-nums">{value}</p>
                                <p className="text-[10px] text-slate-500 font-semibold">{label}</p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-4 space-y-2">
                        {SOCIALS.map((social) => (
                            <div key={social.platform} className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2">
                                <PlatformIcon platform={social.platform} size={30} />
                                <span className="text-[12px] font-semibold text-slate-700 flex-1 min-w-0 truncate">{social.handle}</span>
                                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded-lg flex-shrink-0 tabular-nums">
                                    <Icon path={PATHS.users} className="w-3.5 h-3.5 text-slate-400" />
                                    {social.followers}
                                </span>
                            </div>
                        ))}
                    </div>

                    <div className="mt-4 rounded-2xl border border-primary-200 bg-primary-50/60 p-3.5">
                        <div className="flex items-center gap-2 mb-2">
                            <h4 className="text-[13px] font-black text-slate-800">Rate card</h4>
                            <span className="text-[11px] font-bold text-slate-500">from {usd(450, 0)}</span>
                            <span className="ml-auto text-[10px] font-black text-primary-700 uppercase tracking-wide">Published</span>
                        </div>
                        <div className="space-y-1.5">
                            {RATE_CARD.map((item) => (
                                <div key={item.id} className="flex items-center gap-3 bg-white rounded-xl border border-primary-100 px-3 py-2">
                                    <div className="min-w-0 flex-1">
                                        <p className="text-[12.5px] font-bold text-slate-800 truncate">{item.label}</p>
                                        <p className="text-[10.5px] text-slate-400 truncate">{item.note}</p>
                                    </div>
                                    <span className="text-[13px] font-black text-slate-900 tabular-nums flex-shrink-0">{usd(item.price, 0)}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mt-4 flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50/50 px-3.5 py-3">
                        <span className="text-2xl font-black text-amber-600 tabular-nums flex-shrink-0">4.9</span>
                        <span className="flex text-amber-400 flex-shrink-0" aria-hidden="true">
                            {[0, 1, 2, 3, 4].map((index) => (
                                <svg key={index} viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor">
                                    <path d="M12 2l3 6.5 7 .9-5 4.9 1.2 7L12 18l-6.2 3.3L7 14.3l-5-4.9 7-.9L12 2z" />
                                </svg>
                            ))}
                        </span>
                        <p className="text-[11px] text-slate-600 leading-snug min-w-0">
                            <strong className="text-slate-800">38 paid collaborations.</strong> Delivered before deadline on 31 of them.
                        </p>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2">
                        <span className="contact-btn-metallic text-center">
                            <span className="contact-btn-metallic-inner">Buy a coffee ☕</span>
                        </span>
                        <span className="subscribe-btn-silver text-center">
                            <span className="subscribe-btn-silver-inner">Subscribe $9.99/mo</span>
                        </span>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                        <span className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-slate-900 text-white text-[13px] font-bold">
                            <img src="/star192.png" alt="" className="w-4 h-4 object-contain" /> Send stars
                        </span>
                        <span className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-primary-600 text-white text-[13px] font-bold">
                            <Icon path={PATHS.chat} className="w-4 h-4" /> Message
                        </span>
                    </div>
                </div>
            </div>
        </Frame>
    );
}

/* ────────────────────────────  the product  ──────────────────────────── */

const PROOF_TABS = [
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'campaigns', label: 'Campaigns' },
    { key: 'profile', label: 'Profile' },
];

/** The four ideas the sections this one replaced were carrying, at the weight
 *  a landing page can actually spend on them. The last is the two-way scorer,
 *  reduced to its one true sentence. */
const PROOF_POINTS = [
    'Every cent traceable to its row — hand it to an accountant.',
    'Brand budgets with the money, the deliverable and the deadline up front.',
    'A rate card in public ends the “what do you charge” thread.',
    'Matched on category, platform and audience — and told why, including when a brand’s budget is under your floor.',
];

/**
 * One section where there were three, and one mockup on screen where there
 * were four. The tabs are the whole trick: a visitor who wants to see the
 * ledger can, and a visitor who does not is no longer scrolled past it.
 *
 * The panel keeps a floor under its height so switching tabs cannot pull the
 * page out from under the hand that switched it.
 */
function ProofSection() {
    const [tab, setTab] = useState('dashboard');

    return (
        <section id="product" className="bg-slate-50 py-14 sm:py-20 border-y border-slate-100">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
                <Reveal>
                    <SectionHead eyebrow="The product" title="One ledger, one link, one place the money lands" />
                </Reveal>

                <Reveal delay={60}>
                    <div role="tablist" aria-label="Product screens"
                        className="mx-auto mb-6 w-max max-w-full flex gap-1 rounded-full bg-slate-100 p-1">
                        {PROOF_TABS.map((item) => (
                            <button key={item.key} type="button" role="tab"
                                id={`proof-tab-${item.key}`}
                                aria-selected={tab === item.key}
                                aria-controls={`proof-panel-${item.key}`}
                                onClick={() => setTab(item.key)}
                                className={`px-4 sm:px-6 py-2 rounded-full text-[13px] font-bold transition
                                    focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-100
                                    ${tab === item.key
                                        ? 'bg-white text-slate-900 shadow-[0_6px_18px_-10px_rgba(15,23,42,0.6)]'
                                        : 'text-slate-500 hover:text-slate-800'}`}>
                                {item.label}
                            </button>
                        ))}
                    </div>
                </Reveal>

                <Reveal delay={100}>
                    <div role="tabpanel" id={`proof-panel-${tab}`} aria-labelledby={`proof-tab-${tab}`}
                        className="max-w-2xl mx-auto min-h-[620px] sm:min-h-[760px]">
                        {/* Keyed so the panel remounts and the page's own pop
                            keyframe cross-fades the swap — one that is already
                            switched off under `prefers-reduced-motion`, and that
                            leaves the content visible either way. */}
                        <div key={tab} className="astreona-pop">
                            {tab === 'dashboard' && (
                                <Frame url="astreona.com/finance">
                                    <div className="p-3 sm:p-5 space-y-3 sm:space-y-4">
                                        <HeroBalanceCard />
                                        <BreakdownMock />
                                    </div>
                                </Frame>
                            )}

                            {tab === 'campaigns' && (
                                <Frame url="astreona.com/campaigns">
                                    <div className="p-3 sm:p-5 space-y-3">
                                        <div className="flex items-center justify-between gap-3">
                                            <h3 className="text-sm font-black text-slate-800 uppercase tracking-wide">Showcase · open now</h3>
                                            <span className="text-[11px] font-bold text-primary-700 bg-primary-50 border border-primary-200 px-2.5 py-1 rounded-full flex-shrink-0">
                                                {usd(SHOWCASE.reduce((sum, campaign) => sum + campaign.budget, 0), 0)} on the board
                                            </span>
                                        </div>
                                        {SHOWCASE.map((campaign) => <CampaignCard key={campaign.id} campaign={campaign} />)}
                                        <DealTimeline />
                                    </div>
                                </Frame>
                            )}

                            {tab === 'profile' && <ProfileMock />}
                        </div>
                    </div>
                </Reveal>

                {/* <div className="max-w-3xl mx-auto mt-8 grid sm:grid-cols-2 gap-x-7 gap-y-3">
                    {PROOF_POINTS.map((point, index) => (
                        <Reveal key={point} delay={index * 70}>
                            <div className="flex items-start gap-3">
                                <span className="w-5 h-5 mt-0.5 rounded-full bg-primary-600 text-white flex items-center justify-center flex-shrink-0">
                                    <Icon path={PATHS.check} className="w-3 h-3" strokeWidth={3} />
                                </span>
                                <p className="m-0 text-[13.5px] text-slate-600 leading-relaxed">{point}</p>
                            </div>
                        </Reveal>
                    ))}
                </div> */}
            </div>
        </section>
    );
}

/* ────────────────────────────  trust / escrow  ───────────────────────── */

const TRUST = [
    {
        icon: PATHS.shield, title: 'Funded before you start',
        body: 'Approval debits the brand’s wallet on the spot and locks the amount in escrow. If funding fails, the approval rolls back with it.',
    },
    {
        icon: PATHS.clock, title: 'A stalling brand cannot trap you',
        body: 'Every delivery carries an auto-approval deadline. Let it lapse and the escrow releases in your favour, automatically.',
    },
    {
        icon: PATHS.doc, title: 'Rejections have to be itemised',
        body: 'A revision request with no reason is refused by the system. What comes back is specific enough to act on — and to be read by an arbitrator later.',
    },
];

function TrustSection() {
    return (
        <section id="trust" className="relative overflow-hidden bg-slate-900 py-16 sm:py-24">
            <div className="astreona-trust-glow" aria-hidden="true" />
            <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
                <Reveal>
                    <SectionHead
                        dark
                        eyebrow="How you actually get paid"
                        title="The protections are the product"
                        desc="Anyone can list campaigns. What matters is what happens when one goes sideways — so these rules live in the escrow, not in a support policy."
                    />
                </Reveal>

                {/* Three at full weight rather than six skimmed: each one is
                    a rule that lives in the escrow, and a reader who takes only
                    one of them is better served than one who scans all six. */}
                <div className="grid sm:grid-cols-3 gap-4 sm:gap-5">
                    {TRUST.map((item, index) => (
                        <Reveal key={item.title} delay={index * 60}>
                            <div className="h-full rounded-2xl bg-white/[0.04] border border-white/10 backdrop-blur p-6 sm:p-7 hover:bg-white/[0.07] hover:border-primary-500/40 transition">
                                <span className="inline-flex w-14 h-14 rounded-2xl bg-primary-500/15 text-primary-300 items-center justify-center mb-4">
                                    <Icon path={item.icon} className="w-7 h-7" />
                                </span>
                                <h3 className="text-base font-black text-white">{item.title}</h3>
                                <p className="text-[13.5px] text-slate-300 leading-relaxed mt-2">{item.body}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ────────────────────────────  how it works  ─────────────────────────── */

const STEPS = [
    { n: '01', title: 'Claim your link', body: 'Pick a username and astreona.com/you is yours. Add your platforms and a photo. No approval queue.' },
    { n: '02', title: 'Switch on your streams', body: 'Coffees, a subscription price, a rate card, an inbox open to brands. Each is one toggle, live immediately.' },
    // Was: '… At $1,000 you can request a payout …' — the threshold figure is no longer quoted anywhere on the site.
    { n: '03', title: 'Get paid, then paid out', body: 'All four streams land on one ledger. Request a payout to your bank or PayPal and watch it go from pending to paid.' },
];

function HowSection() {
    return (
        <section className="bg-slate-50 py-14 sm:py-20 border-y border-slate-100">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
                <Reveal>
                    <SectionHead eyebrow="Getting started" title="Three steps, one afternoon" />
                </Reveal>
                <div className="grid md:grid-cols-3 gap-4 sm:gap-5">
                    {STEPS.map((step, index) => (
                        <Reveal key={step.n} delay={index * 80}>
                            <div className="relative h-full rounded-3xl bg-white border border-slate-200 p-6 overflow-hidden">
                                <span className="absolute -top-3 -right-1 text-[76px] font-black text-slate-100 leading-none select-none" aria-hidden="true">
                                    {step.n}
                                </span>
                                <span className="relative inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-gradient-to-br from-primary-400 to-primary-700 text-white font-black mb-4">
                                    {index + 1}
                                </span>
                                <h3 className="relative text-lg font-black text-slate-900">{step.title}</h3>
                                <p className="relative text-[13.5px] text-slate-600 leading-relaxed mt-2">{step.body}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ─────────────────────────  the apps (coming soon)  ──────────────────── */

/**
 * Both stores, one line, and it says "not yet" in plain words.
 *
 * The badges are **not links and not buttons**: there is nothing on the other
 * side of them today. A badge shaped like the real thing that navigates
 * nowhere — or worse, to a store page that 404s — costs more trust than the
 * announcement wins, so they carry `aria-disabled` and no handler. Turning one
 * into a store link is a one-line change when the build ships.
 *
 * The marks come from `ionicons` (`logo-apple`, `logo-google-playstore`), the
 * same generated source `utils/platformIcons` uses, for the reason given
 * there: a brand path reproduced by hand renders as a blob and nobody notices
 * until production.
 */
const STORE_BADGES = [
    {
        key: 'ios',
        top: 'Coming soon to the',
        store: 'App Store',
        shapes: [
            'M349.13 136.86c-40.32 0-57.36 19.24-85.44 19.24-28.79 0-50.75-19.1-85.69-19.1-34.2 0-70.67 20.88-93.83 56.45-32.52 50.16-27 144.63 25.67 225.11 18.84 28.81 44 61.12 77 61.47h.6c28.68 0 37.2-18.78 76.67-19h.6c38.88 0 46.68 18.89 75.24 18.89h.6c33-.35 59.51-36.15 78.35-64.85 13.56-20.64 18.6-31 29-54.35-76.19-28.92-88.43-136.93-13.08-178.34-23-28.8-55.32-45.48-85.79-45.48z',
            'M340.25 32c-24 1.63-52 16.91-68.4 36.86-14.88 18.08-27.12 44.9-22.32 70.91h1.92c25.56 0 51.72-15.39 67-35.11 14.72-18.77 25.88-45.37 21.8-72.66z',
        ],
    },
    {
        key: 'android',
        top: 'Coming soon on',
        store: 'Google Play',
        shapes: [
            'M48 59.49v393a4.33 4.33 0 007.37 3.07L260 256 55.37 56.42A4.33 4.33 0 0048 59.49zM345.8 174L89.22 32.64l-.16-.09c-4.42-2.4-8.62 3.58-5 7.06l201.13 192.32zM84.08 472.39c-3.64 3.48.56 9.46 5 7.06l.16-.09L345.8 338l-60.61-57.95zM449.38 231l-71.65-39.46L310.36 256l67.37 64.43L449.38 281c19.49-10.77 19.49-39.23 0-50z',
        ],
    },
];

function StoreBadge({ badge, compact = false }) {
    return (
        <span aria-disabled="true"
            className={`inline-flex items-center rounded-2xl border border-white/15 bg-white/[0.06] select-none
                ${compact ? 'gap-2 px-3 py-1.5' : 'gap-3 px-4 py-2.5 sm:px-5 sm:py-3'}`}>
            <svg viewBox="0 0 512 512" fill="currentColor" aria-hidden="true"
                className={`text-white flex-shrink-0 ${compact ? 'w-4 h-4' : 'w-7 h-7'}`}>
                {badge.shapes.map((shape) => <path key={shape} d={shape} />)}
            </svg>
            <span className="text-left leading-tight">
                <span className={`block font-semibold text-white/55 ${compact ? 'text-[8.5px]' : 'text-[10px]'}`}>
                    {badge.top}
                </span>
                <span className={`block font-black text-white ${compact ? 'text-[11.5px]' : 'text-[15px] sm:text-base'}`}>
                    {badge.store}
                </span>
            </span>
            {!compact && (
                <span className="ml-1 px-2 py-0.5 rounded-full bg-primary-400/15 border border-primary-400/30
                        text-[9.5px] font-black uppercase tracking-[0.12em] text-primary-200 whitespace-nowrap">
                    Soon
                </span>
            )}
        </span>
    );
}

/** A lock screen rather than a screenshot of a screen: push is the one thing
 *  the apps add over the site people can already use, so it is what the mock
 *  shows. Every figure here matches the example account the rest of the page
 *  quotes — the $4,500 campaign, the 100 ★ gift, the 94% match. */
/**
 * What is left of the app announcement. A "coming soon" section asks the
 * reader to be patient before they have been given a reason to care, so the
 * apps get one line — and it renders inside the closing CTA rather than beside
 * it, on the one dark ground, where a visitor who has just decided to sign up
 * is exactly who wants to know their account will carry over.
 *
 * `id="apps"` because the footer sends people here looking for the badges.
 */
function AppStrip() {
    return (
        <div id="apps" className="relative max-w-3xl mx-auto px-4 sm:px-6 pb-12 sm:pb-16">
            <Reveal>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-center sm:text-left">
                    <p className="m-0 text-[13.5px] text-slate-400 leading-relaxed">
                        iOS and Android are in the last stretch. Same account, same balance, same ledger.
                    </p>
                    <span className="flex flex-wrap justify-center gap-2 flex-shrink-0">
                        {STORE_BADGES.map((badge) => <StoreBadge key={badge.key} badge={badge} compact />)}
                    </span>
                </div>
            </Reveal>
        </div>
    );
}

/* TestimonialSection — reinstate with real, consented quotes.
   The three that were here were attributed to invented people with invented
   earnings figures. An "example account" chip on a dashboard is an honest
   illustration; a fabricated human is not the same thing, and this page's
   whole credibility rests on its numbers being checkable. */

/* ────────────────────────────────  faq  ──────────────────────────────── */

function FaqSection() {
    return (
        <section id="faq" className="bg-slate-50 py-16 sm:py-24 border-t border-slate-100">
            <div className="max-w-3xl mx-auto px-4 sm:px-6">
                <Reveal>
                    <SectionHead eyebrow="FAQ" title="The questions that decide it" />
                </Reveal>
                <div className="space-y-3">
                    {FAQ.map((item, index) => (
                        <Reveal key={item.q} delay={index * 50}>
                            <details className="group rounded-2xl bg-white border border-slate-200 overflow-hidden">
                                <summary className="flex items-center gap-3 px-5 py-4 cursor-pointer list-none select-none">
                                    <span className="text-[14.5px] font-bold text-slate-800 flex-1">{item.q}</span>
                                    <span className="w-7 h-7 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center flex-shrink-0 transition group-open:rotate-45 group-open:bg-primary-100 group-open:text-primary-700">
                                        <Icon path="M12 5v14M5 12h14" className="w-4 h-4" />
                                    </span>
                                </summary>
                                <p className="px-5 pb-5 -mt-1 text-[13.5px] text-slate-600 leading-relaxed m-0">{item.a}</p>
                            </details>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ──────────────────────────  final CTA + footer  ─────────────────────── */

function FinalCta({ onSignup }) {
    return (
        <section className="relative overflow-hidden bg-slate-900 py-20 sm:py-28">
            <div className="astreona-cta-glow" aria-hidden="true" />
            <AppStrip />
            <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
                <Reveal>
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-[11px] font-bold text-primary-200 uppercase tracking-[0.14em]">
                        <Icon path={PATHS.sparkle} className="w-3.5 h-3.5" /> Free to start
                    </span>
                    <h2 className="mt-6 text-[32px] sm:text-5xl font-black text-white tracking-tight leading-[1.08]">
                        One link.
                        <br />
                        <span className="astreona-gradient-text">Start collecting.</span>
                    </h2>
                    <p className="mt-5 text-[15px] sm:text-lg text-slate-300 leading-relaxed">
                        Claim it, switch on the streams that fit you, let the ledger do the rest.{' '}
                        {FEE_PERCENT}% commission, no monthly fee, no lock-in — what you earn is what you keep.
                    </p>
                    {/* The claim above is only worth what it is worth next to
                        somebody else's, so the closing line is an anchor back
                        to the table rather than one more adjective. */}
                    <p className="mt-3 text-[13px] text-slate-400 leading-relaxed">
                        Patreon keeps up to {usd((COMPARISON_BASE * PATREON_MAX_CUT) / 100, 0)} of
                        that {usd(COMPARISON_BASE, 0)}. Linktree keeps {usd(comparisonRow('Linktree').lost, 0)}.
                        We keep {FEE_PERCENT === 0 ? 'nothing' : usd(feeOf(COMPARISON_BASE), 0)}.{' '}
                        <a href="#compare" className="font-bold text-primary-300 hover:text-primary-200 border-b border-primary-400/40">
                            see the table
                        </a>
                    </p>
                    <button type="button" onClick={onSignup}
                        className="group mt-8 inline-flex items-center justify-center gap-2 px-9 py-4 rounded-full text-base font-black text-white
                            bg-gradient-to-r from-primary-400 to-primary-600
                            shadow-[0_18px_50px_-16px_rgba(45,217,195,0.95)] hover:-translate-y-0.5 active:translate-y-0 transition">
                        Create your free account
                        <Icon path={PATHS.arrow} className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                    </button>
                    <p className="mt-4 text-[12px] text-slate-400">Takes about five minutes · no card required</p>
                </Reveal>
            </div>
        </section>
    );
}

function Footer({ onLogin, onSignup }) {
    const columns = [
        {
            title: 'Product',
            links: [
                { label: 'Ways to earn', href: '#earn' },
                { label: 'Dashboard', href: '#product' },
                { label: 'Campaigns', href: '#marketplace' },
                // { label: 'How you get paid', href: '#trust' },
                // { label: 'Mobile apps', href: '#apps' },
            ],
        },
        {
            title: 'Company',
            links: [
                { label: 'Contact us', href: '/contactus' },
                { label: 'contact@astreona.com', href: 'mailto:contact@astreona.com' },
                { label: 'FAQ', href: '#faq' },
            ],
        },
        {
            title: 'Legal',
            links: [
                { label: 'Terms of Use', href: '/termsofuse' },
                { label: 'Privacy Policy', href: '/privacypolicy' },
            ],
        },
    ];

    return (
        <footer className="bg-slate-950 text-slate-400 pt-14 pb-8">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
                <div className="grid sm:grid-cols-2 lg:grid-cols-[1.4fr,repeat(3,1fr)] gap-8 lg:gap-6">
                    <div>
                        <Wordmark dark />
                        <p className="mt-3.5 text-[13px] leading-relaxed max-w-xs">
                            One link, four ways to get paid — campaigns, subscriptions,
                            coffees and star gifts, on a single ledger.
                        </p>
                        <div className="mt-5 flex gap-2">
                            <button type="button" onClick={onSignup}
                                className="px-4 py-2 rounded-full bg-primary-600 hover:bg-primary-500 text-white text-[13px] font-bold transition">
                                Sign up
                            </button>
                            <button type="button" onClick={onLogin}
                                className="px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white text-[13px] font-bold transition">
                                Log in
                            </button>
                        </div>

                        {/* The same not-yet badges as the app band, small. This
                            is where a visitor goes looking for them once they
                            have scrolled past the section that announces it. */}
                        <div className="mt-6">
                            <p className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500 mb-2">
                                Apps coming soon
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {STORE_BADGES.map((badge) => <StoreBadge key={badge.key} badge={badge} compact />)}
                            </div>
                        </div>
                    </div>

                    {columns.map((column) => (
                        <div key={column.title}>
                            <p className="text-[11px] font-black text-white uppercase tracking-[0.14em] mb-3">{column.title}</p>
                            <ul className="list-none p-0 m-0 space-y-2">
                                {column.links.map((link) => (
                                    <li key={link.label}>
                                        <a href={link.href} className="text-[13px] text-slate-400 hover:text-primary-300 no-underline transition">
                                            {link.label}
                                        </a>
                                    </li>
                                ))}

                            </ul>
                        </div>
                    ))}
                </div>

                <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <p className="text-[12px] m-0">© {new Date().getFullYear()} Astreona. All rights reserved.</p>
                    <p className="text-[12px] m-0 text-slate-500">
                        Figures shown on this page are illustrative examples from a demo account.
                    </p>
                </div>
            </div>
        </footer>
    );
}

/* ────────────────────────────────  page  ─────────────────────────────── */

const STYLES = `
.astreona-landing { font-family: Inter, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; }

/* Same soft mint-to-white wash as the star wallet card in the app, so the
   first thing a visitor sees is a surface they will meet again inside. */
.astreona-hero {
    background:
        radial-gradient(900px 520px at 88% 8%, rgba(32,201,181,0.16), transparent 62%),
        linear-gradient(135deg, #e6fcf9 0%, #f2fdfb 46%, #ffffff 100%);
}
.astreona-hero-glow {
    position: absolute; top: -34%; left: 46%; width: 900px; height: 900px;
    transform: translateX(-50%); pointer-events: none;
    background: radial-gradient(circle, rgba(32,201,181,0.14) 0%, transparent 62%);
}
.astreona-hero-grid {
    position: absolute; inset: 0; pointer-events: none; opacity: 0.7;
    background-image:
        linear-gradient(rgba(15,23,42,0.035) 1px, transparent 1px),
        linear-gradient(90deg, rgba(15,23,42,0.035) 1px, transparent 1px);
    background-size: 64px 64px;
    mask-image: radial-gradient(ellipse 90% 65% at 50% 35%, #000 25%, transparent 78%);
    -webkit-mask-image: radial-gradient(ellipse 90% 65% at 50% 35%, #000 25%, transparent 78%);
}

/* Two variants: the pale one only has contrast on the dark sections, the deep
   one only has contrast on the light hero. Using either in the wrong place is
   the whole reason they are separate classes. */
.astreona-gradient-text {
    background: linear-gradient(100deg, #4de7d2 0%, #24d4be 45%, #9df3e6 100%);
    -webkit-background-clip: text; background-clip: text; color: transparent;
}
.astreona-gradient-ink {
    background: linear-gradient(100deg, #17b3a1 0%, #0f8a7e 52%, #14a394 100%);
    -webkit-background-clip: text; background-clip: text; color: transparent;
}

.astreona-trust-glow, .astreona-cta-glow {
    position: absolute; pointer-events: none;
    background: radial-gradient(circle, rgba(36,212,190,0.18) 0%, transparent 65%);
}
.astreona-trust-glow { top: -40%; right: -10%; width: 760px; height: 760px; }
.astreona-cta-glow   { bottom: -55%; left: 50%; transform: translateX(-50%); width: 900px; height: 900px; }

@keyframes astreonaFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }
.astreona-float { animation: astreonaFloat 7s ease-in-out infinite; }

/* Hero stage: the flow lines drift, the firing sender pings, arrivals rise. */
@keyframes astreonaFlow { to { stroke-dashoffset: -14; } }
.astreona-flow { animation: astreonaFlow 1.1s linear infinite; }

@keyframes astreonaPing {
    0%   { transform: scale(1);    opacity: .8; }
    100% { transform: scale(1.55); opacity: 0; }
}
.astreona-ping { animation: astreonaPing .9s cubic-bezier(0,0,.2,1) infinite; }

@keyframes astreonaRise {
    0%   { opacity: 0; transform: translate(-50%, 6px)   scale(.8); }
    18%  { opacity: 1; transform: translate(-50%, -10px) scale(1); }
    70%  { opacity: 1; transform: translate(-50%, -28px) scale(1); }
    100% { opacity: 0; transform: translate(-50%, -46px) scale(.94); }
}
.astreona-rise { animation: astreonaRise 1.3s cubic-bezier(.22,.68,0,1) forwards; }

@keyframes astreonaPop { 0% { opacity: 0; transform: scale(.9) translateY(8px); } 100% { opacity: 1; transform: none; } }
.astreona-pop { animation: astreonaPop .5s cubic-bezier(.22,1.4,.4,1) both; }

/* A link taking its place on the phone. Opaque from the first frame — it is
   taking over from the token that just flew in, not appearing out of nothing. */
@keyframes astreonaSettle { 0% { transform: scale(1); } 45% { transform: scale(1.2); } 100% { transform: scale(1); } }
.astreona-settle { animation: astreonaSettle .34s cubic-bezier(.3,1.1,.4,1) both; }

@keyframes astreonaTwinkle { 0%,100% { transform: scale(1) rotate(0deg); } 50% { transform: scale(1.14) rotate(9deg); } }
.astreona-twinkle { animation: astreonaTwinkle 2.4s ease-in-out infinite; }

/* The marketplace band: the hero's wash again at a fraction of the strength,
   so three rows of white cards sitting between two white sections still read
   as their own part of the page. */
.astreona-market {
    background:
        radial-gradient(900px 420px at 50% -12%, rgba(32,201,181,0.11), transparent 70%),
        linear-gradient(180deg, #ffffff 0%, #f4fdfb 46%, #ffffff 100%);
}

/* A row that scrolls forever. The track is the same group twice, so sliding
   it half its own width lands the copy exactly where the original started —
   which only holds while both groups are identical, hence the trailing gap
   living on the group rather than between the two of them. The vertical
   padding is there so overflow:hidden clips the row and not the shadows
   under the cards inside it. */
.astreona-marquee {
    overflow: hidden;
    -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 5%, #000 95%, transparent 100%);
    mask-image: linear-gradient(90deg, transparent 0, #000 5%, #000 95%, transparent 100%);
}
.astreona-marquee-track {
    display: flex; width: max-content;
    animation-name: astreonaMarquee;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
}
.astreona-marquee-group {
    display: flex; align-items: stretch; gap: 12px; padding: 10px 12px 16px 0;
}
@keyframes astreonaMarquee { to { transform: translateX(-50%); } }

.astreona-landing summary::-webkit-details-marker { display: none; }
.astreona-landing a { text-decoration: none; }
.astreona-landing details[open] summary { border-bottom: 1px solid #f1f5f9; }

html { scroll-behavior: smooth; }

@media (prefers-reduced-motion: reduce) {
    html { scroll-behavior: auto; }
    .astreona-float, .astreona-pop, .astreona-twinkle,
    .astreona-flow, .astreona-ping, .astreona-rise, .astreona-settle { animation: none; }

    /* The rows become a plain scroller the reader drives. The second group is
       only there to make the loop seamless; with no loop it would just be the
       same cards listed twice. */
    .astreona-marquee { overflow-x: auto; -webkit-mask-image: none; mask-image: none; }
    .astreona-marquee-track { animation: none; }
    .astreona-marquee-group + .astreona-marquee-group { display: none; }
}
`;

/**
 * The dialog **never opens on its own** — not on `/`, and not on `/login` or
 * `/register` either. Arriving at a page and being handed a form nobody asked
 * for is the fastest way to lose a first-time visitor, and it hid the page
 * this one exists to show. Every route renders the landing page as it is; the
 * header, hero, footer and final CTA all carry buttons that open it.
 */
function Landing() {
    const [authView, setAuthView] = useState(null);

    if (isAuthenticated()) return <Navigate to="/profile" replace />;

    return (
        <div className="astreona-landing bg-white">
            <style>{STYLES}</style>

            <TopNav onLogin={() => setAuthView('login')} onSignup={() => setAuthView('signup')} />

            <main>
                <Hero onSignup={() => setAuthView('signup')} onLogin={() => setAuthView('login')} />
                <PlatformStrip />
                <MarketplaceSection />
                <ComparisonSection onSignup={() => setAuthView('signup')} />
                <EarnSection />
                <ProofSection />
                {/* <TrustSection /> */}
                {/* <HowSection /> */}
                <FaqSection />
                {/* <FinalCta onSignup={() => setAuthView('signup')} /> */}
            </main>

            <Footer onLogin={() => setAuthView('login')} onSignup={() => setAuthView('signup')} />

            <AuthModal view={authView} onView={setAuthView} />
        </div>
    );
}

export default Landing;
